import Cookies from "js-cookie";
import {useMemo} from "react";
import useSWR from "swr";

import {ApiError, apiFetcher, friendlyErrorMessage} from "@lib/fetcher";

// ── Hardened mutation fetch ──────────────────────────────────

const MUTATION_TIMEOUT_MS = 60_000;
const MUTATION_MAX_ATTEMPTS = 2;
const RETRYABLE_STATUSES = new Set([408, 429, 502, 503, 504]);

async function mutationFetch<T>(
    url: string,
    opts: {method: string; body?: unknown; errorLabel: string},
): Promise<T> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const headers: Record<string, string> = {"X-CSRFToken": csrfToken};
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";

    let lastError: unknown = null;
    for (let attempt = 1; attempt <= MUTATION_MAX_ATTEMPTS; attempt++) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), MUTATION_TIMEOUT_MS);
        try {
            const response = await fetch(url, {
                method: opts.method,
                headers,
                credentials: "include",
                body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
                signal: controller.signal,
            });
            clearTimeout(timer);

            if (!response.ok) {
                let msg = opts.errorLabel;
                try {
                    const json = await response.json();
                    const errors = (json.errors as string[]) ?? [];
                    if (errors[0]) msg = errors[0];
                } catch { /* ignore parse failure */ }

                if (attempt < MUTATION_MAX_ATTEMPTS && RETRYABLE_STATUSES.has(response.status)) {
                    await new Promise((r) => setTimeout(r, 500 * attempt));
                    continue;
                }
                const kind = response.status === 401 ? "auth"
                    : response.status === 403 ? "permission"
                    : response.status >= 500 ? "server" : "unknown";
                if (response.status === 401) msg = "Session expired — please log in again.";
                if (response.status === 403) msg = "Permission denied — your Xero connection may have expired.";
                throw new ApiError(msg, kind, response.status);
            }

            const json = await response.json();
            return (json.data ?? json) as T;
        } catch (err) {
            clearTimeout(timer);
            lastError = err;
            if (err instanceof ApiError) throw err;
            const isTransient =
                err instanceof Error &&
                (err.name === "AbortError" || err.message === "Failed to fetch" || err.message === "Load failed");
            if (attempt < MUTATION_MAX_ATTEMPTS && isTransient) {
                await new Promise((r) => setTimeout(r, 500 * attempt));
                continue;
            }
            if (err instanceof Error && err.name === "AbortError") {
                throw new ApiError("Request timed out — please try again.", "network");
            }
            throw new ApiError("Network error — check your connection and try again.", "network");
        }
    }
    throw lastError instanceof ApiError ? lastError : new ApiError("Network error", "network");
}

// ── Types ─────────────────────────────────────────────────────

export interface VNextTab {
    id: string;
    key: string;
    label: string;
    ordinal: number;
    is_visible: boolean;
    config: Record<string, unknown>;
    has_context?: boolean;
}

export interface VNextScenario {
    id: string;
    name: string;
    is_default: boolean;
    baseline_method: string;
    assumptions?: Record<string, number>;
    created_at: string;
    updated_at: string;
}

export interface VNextWorkspace {
    id: string;
    name: string;
    description: string;
    xero_tenant_id: string;
    created_at: string;
    updated_at: string;
}

export interface VNextWorkspaceData {
    workspace: VNextWorkspace;
    tabs: VNextTab[];
    scenarios: VNextScenario[];
}

export interface VNextCategoryMapping {
    id: string;
    domain: string;
    source_type: string;
    source_id: string;
    source_label: string;
    category_key: string;
    driver_type: string;
    allocation_rule: Record<string, unknown>;
    status: "proposed" | "approved" | "rejected";
    confidence: number;
    proposed_by: "human" | "agent";
    approved_by: string | null;
    created_at: string;
    updated_at: string;
}

export interface VNextOpApplied {
    op_id: string;
    label: string;
    op_type: string;
    driver_type?: string;
    value: string;
    effect: string;
}

export interface VNextRow {
    node_id: string;
    label: string;
    category_key: string;
    value: number;
    status: "actual" | "forecast";
    baseline_value?: number;
    is_override?: boolean;
    ops_applied?: VNextOpApplied[];
    xero_account_id?: string;
    linked_from?: "pnl" | "cf";
    parent_group?: string;
    account_group?: string;
}

export interface VNextMonth {
    month_key: string;
    label: string;
    status: "actual" | "forecast";
    rows: VNextRow[];
}

export interface VNextKPI {
    key: string;
    label: string;
    actual_total: number;
    forecast_total: number;
    baseline: number;
}

export interface VNextOp {
    id: string;
    op_type: "set_baseline" | "set_driver" | "set_override" | "set_weekly_ovr";
    target_node_id: string | null;
    target_month: string;
    label: string;
    params: Record<string, unknown>;
    ordinal: number;
    is_active: boolean;
    created_by: "human" | "agent";
    created_at: string;
    target_node_label?: string;
    target_category?: string;
}

export interface VNextOpInput {
    op_type: "set_baseline" | "set_driver" | "set_override";
    target_node_id?: string;
    target_month?: string;
    label?: string;
    params: Record<string, unknown>;
}

export interface VNextCellProvenance {
    value: string;
    baseline: string;
    is_override: boolean;
    ops_applied: {
        op_id: string;
        label: string;
        op_type: string;
        driver_type?: string;
        value: string;
        effect: string;
    }[];
    calculation: string;
}

export interface VNextComparisonBlock {
    mode: "yoy" | "prev";
    period_label: string;
    months: VNextMonth[];
}

export interface VNextComparisonKPIs {
    mode: "yoy" | "prev";
    period_label: string;
    kpis: VNextKPI[];
}

export interface VNextSummaryData {
    scenario: VNextScenario;
    kpis: VNextKPI[];
    baseline_totals: Record<string, number>;
    n_actual_months: number;
    n_forecast_months: number;
    comparison_kpis?: VNextComparisonKPIs;
}

export interface VNextModelData {
    scenario: VNextScenario;
    months: VNextMonth[];
    baseline_totals: Record<string, number>;
    ops_count: number;
    comparison?: VNextComparisonBlock;
    checks_scope?: "full_model";
    xero_check_figures?: PnLCheckFigure[];
}

export interface VNextPreviewDelta {
    cell: string;
    node_label: string;
    month_key: string;
    before: string;
    after: string;
    delta: string;
    delta_pct: string | null;
}

export interface VNextPreviewResult {
    deltas: VNextPreviewDelta[];
    summary_deltas: Record<string, {before: string; after: string; delta: string}>;
    after: {
        months: VNextMonth[];
        baseline_totals: Record<string, number>;
        cell_provenance: Record<string, VNextCellProvenance>;
    };
}

export interface VNextBreakdownData {
    scenario: VNextScenario;
    categories: Record<string, {label: string; months: Record<string, number>}>;
    month_keys: string[];
}

export interface ThreeStatementSourceRef {
    source_type: string;
    source_label?: string | null;
    source_id?: string | null;
    account_code?: string | null;
    account_name?: string | null;
    sync_timestamp?: string | null;
    amount?: number | null;
    drilldown_target?: string | null;
}

export interface ThreeStatementAssumptionRef {
    key: string;
    label: string;
    value: string | number | boolean | null;
    unit?: string | null;
    description?: string | null;
}

export interface ThreeStatementScheduleRef {
    key: string;
    label: string;
    month_key?: string | null;
    value?: number | null;
    description?: string | null;
}

export interface ThreeStatementDependencyRef {
    statement: "pnl" | "bs" | "cf" | "schedule" | "system";
    node_id?: string | null;
    node_label: string;
    month_key?: string | null;
    value?: number | null;
    relation?: string | null;
}

export interface ThreeStatementCheck {
    key: string;
    label: string;
    status: "PASS" | "WARN" | "FAIL";
    severity?: "INFO" | "WARN" | "ERROR";
    statement?: "pnl" | "bs" | "cf" | "system";
    month_key?: string | null;
    delta?: number | null;
    threshold?: number | null;
    reason?: string | null;
    details?: string | null;
}

export interface ThreeStatementWarning {
    message: string;
    code?: string | null;
    level?: "INFO" | "WARN" | "ERROR";
    statement?: "pnl" | "bs" | "cf" | "system" | null;
    month_key?: string | null;
}

export interface ThreeStatementCellProvenance {
    id: string;
    node_id: string;
    month_key: string;
    value: number;
    baseline_value: number;
    is_override: boolean;
    ops_applied: Record<string, unknown>[];
    calculation: string;
    computed_at: string;
    cell_type?: "actual" | "forecast" | "derived" | "override";
    source_type?: string | null;
    source_label?: string | null;
    source_refs?: ThreeStatementSourceRef[];
    assumptions_used?: ThreeStatementAssumptionRef[];
    schedule_refs?: ThreeStatementScheduleRef[];
    linked_dependencies?: ThreeStatementDependencyRef[];
    checks?: ThreeStatementCheck[];
}

export interface VNextProvenanceData {
    node_id: string;
    label: string;
    month_key: string;
    provenance: ThreeStatementCellProvenance | null;
    lineage: {
        id: string;
        source_type: string;
        source_id: string;
        amount: number;
        description: string;
    }[];
}

export interface VNextDrilldownData {
    node_id: string;
    label: string;
    month_key: string;
    // Leaf node fields
    value?: number;
    source?: string | null;
    lineage?: {id: string; source_type: string; source_id: string; amount: number; description: string}[];
    // Aggregation node fields
    breakdown?: {node_id: string; label: string; category_key: string; value: number; weight: number}[];
    total?: number;
    has_more?: boolean;
}

export type WorkbenchDrawerDrilldownLevel =
    | "account"
    | "segment"
    | "customer_parent"
    | "customer_child"
    | "transactions";

export type WorkbenchDrawerDrilldownStatus =
    | "reconciled"
    | "immaterial_rounding"
    | "variance";

export type WorkbenchDrawerResidualType =
    | "unassigned_no_contact"
    | "immaterial_rounding"
    | "unreconciled_other";

export interface WorkbenchDrawerTransactionFilters {
    month_key: string;
    account_id: string;
    account_name: string;
    contact_id: string;
    segment: string;
    source: string;
}

export interface WorkbenchDrawerAvailableLevel {
    level: Exclude<WorkbenchDrawerDrilldownLevel, "account">;
    coverage_pct: number;
}

export interface WorkbenchDrawerChildRow {
    key: string;
    label: string;
    value: number;
    contact_id?: string;
    txn_count?: number;
    is_residual: boolean;
    residual_type?: WorkbenchDrawerResidualType;
    drillable: boolean;
    has_children?: boolean;
    next_level?: "customer_child" | "transactions";
    filters_for_transactions?: WorkbenchDrawerTransactionFilters;
}

export interface WorkbenchDrawerPagination {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
}

export interface WorkbenchDrawerAccountData {
    level: "account";
    node_id: string;
    label: string;
    month_key: string;
    display_total: number;
    line_count: number;
    available_levels: WorkbenchDrawerAvailableLevel[];
    filters_for_transactions: WorkbenchDrawerTransactionFilters;
}

export interface WorkbenchDrawerBreakdownData {
    level: "segment" | "customer_parent" | "customer_child";
    node_id: string;
    label: string;
    month_key: string;
    display_total?: number;
    explained_sum?: number;
    residual_total?: number;
    raw_variance?: number;
    status?: WorkbenchDrawerDrilldownStatus;
    children?: WorkbenchDrawerChildRow[];
    available?: boolean;
    reason?: string;
    next_level?: "transactions";
    parent_contact_id?: string;
    pagination?: WorkbenchDrawerPagination;
    filters_for_transactions: WorkbenchDrawerTransactionFilters;
}

export interface WorkbenchDrawerTransactionRow {
    line_id: string;
    date: string;
    description: string;
    account_code: string;
    account_name: string;
    contact_id: string;
    contact_name: string;
    net_amount: number;
    source_type: string;
    reference: string;
    invoice_number?: string;
    invoice_type?: string;
    invoice_contact?: string;
}

export interface WorkbenchDrawerTransactionsData {
    level: "transactions";
    detail_source?: "journal" | "invoice";
    node_id: string;
    label: string;
    month_key: string;
    transaction_sum: number;
    page_sum: number;
    status: WorkbenchDrawerDrilldownStatus;
    rows: WorkbenchDrawerTransactionRow[];
    pagination: WorkbenchDrawerPagination;
    filters_for_transactions: WorkbenchDrawerTransactionFilters;
}

export type WorkbenchDrawerDrilldownData =
    | WorkbenchDrawerAccountData
    | WorkbenchDrawerBreakdownData
    | WorkbenchDrawerTransactionsData;

// ── Period Options ──────────────────────────────────────────────

export interface PeriodOptions {
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    compare?: string;
    fyStartMonth?: number;
    currentMonth?: string;
}

function appendPeriodParams(params: URLSearchParams, opts?: PeriodOptions) {
    if (!opts) return;
    if (opts.period) params.set("period", opts.period);
    if (opts.selectedMonth) params.set("selected_month", opts.selectedMonth);
    if (opts.customFrom) params.set("custom_from", opts.customFrom);
    if (opts.customTo) params.set("custom_to", opts.customTo);
    if (opts.compare && opts.compare !== "none") params.set("compare", opts.compare);
    if (opts.fyStartMonth && opts.fyStartMonth > 1) params.set("fy_start_month", String(opts.fyStartMonth));
    if (opts.currentMonth) params.set("current_month", opts.currentMonth);
}

// ── SWR Hooks ──────────────────────────────────────────────────

export function useVNextWorkspace(tenantId?: string) {
    // Include tenantId in the SWR key so the cache is busted when the user
    // switches Xero tenants. The backend resolves the workspace from the
    // user's OAuth config — tenantId is only used as a cache-key differentiator.
    const key = tenantId
        ? `/api/model-vnext/workspace/?tenant=${tenantId}`
        : "/api/model-vnext/workspace/";

    const {data, error, isLoading, isValidating, mutate} = useSWR<VNextWorkspaceData>(
        key,
        apiFetcher,
        {
            dedupingInterval: 60000,
            errorRetryCount: 3,
            keepPreviousData: true,
        },
    );

    const showError = error && !(isValidating && data);

    return {
        workspace: data?.workspace ?? null,
        tabs: data?.tabs ?? [],
        scenarios: data?.scenarios ?? [],
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        errorKind: showError && error instanceof ApiError ? error.kind : null,
        refresh: mutate,
    };
}

export async function saveTabConfig(
    workspaceId: string,
    tabId: string,
    config: Record<string, unknown>,
): Promise<void> {
    const csrfToken = Cookies.get("csrftoken") || "";
    await fetch(`/api/model-vnext/workspace/${workspaceId}/tabs/`, {
        method: "PATCH",
        headers: {"X-CSRFToken": csrfToken, "Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({tabs: [{id: tabId, config}]}),
    });
}

export function useVNextMappings(domain?: string, mappingStatus?: string) {
    const params = new URLSearchParams();
    if (domain) params.set("domain", domain);
    if (mappingStatus) params.set("status", mappingStatus);
    const url = `/api/model-vnext/mappings/?${params}`;

    const {data, error, isLoading, mutate} = useSWR<{mappings: VNextCategoryMapping[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );

    return {
        mappings: data?.mappings ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextSummary(workspaceId: string | null, scenarioId?: string, months = 6, actualMonths?: number, periodOpts?: PeriodOptions) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (actualMonths) params.set("actual_months", String(actualMonths));
    appendPeriodParams(params, periodOpts);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/summary/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<VNextSummaryData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        comparisonKpis: data?.comparison_kpis ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextPnL(workspaceId: string | null, scenarioId?: string, months = 6, actualMonths?: number, periodOpts?: PeriodOptions) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (actualMonths) params.set("actual_months", String(actualMonths));
    appendPeriodParams(params, periodOpts);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/pnl/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<VNextModelData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        months: data?.months ?? [],
        comparison: data?.comparison ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextCashFlow(workspaceId: string | null, scenarioId?: string, months = 6, actualMonths?: number, periodOpts?: PeriodOptions) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (actualMonths) params.set("actual_months", String(actualMonths));
    appendPeriodParams(params, periodOpts);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/cashflow/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<VNextModelData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        months: data?.months ?? [],
        comparison: data?.comparison ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextBS(workspaceId: string | null, scenarioId?: string, months = 6, actualMonths?: number, periodOpts?: PeriodOptions) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (actualMonths) params.set("actual_months", String(actualMonths));
    appendPeriodParams(params, periodOpts);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/bs/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<VNextModelData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        months: data?.months ?? [],
        comparison: data?.comparison ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Xero P&L Types ──────────────────────────────────────────

export interface XeroPnLMeta {
    workspace_name: string;
    scenario_name: string;
    period_label: string;
    n_actual_months: number;
    n_forecast_months: number;
    generated_at: string;
}

export interface XeroPnLKpiCard {
    key: string;
    label: string;
    value: number | null;
    delta_pct: number | null;
    delta?: number | null;
    vs_label: string;
}

export interface XeroPnLCogsAccount {
    account_id: string;
    account_name: string;
    total: number;
    pct_of_cogs: number;
    monthly: Record<string, number>;
}

export interface XeroPnLOpexHighlight {
    account_id: string;
    account_name: string;
    total: number;
    pct_of_opex: number;
    mom_delta_pct: number | null;
}

export interface XeroPnLMonthlyPerf {
    month_key: string;
    status: string;
    revenue: number;
    costs: number;
    gross_profit: number;
    net_profit: number;
    gross_margin_pct: number | null;
    net_margin_pct: number | null;
}

export interface XeroPnLMarginGap {
    current_gross_margin_pct: number | null;
    target_gross_margin_pct: number | null;
    gap_pct: number | null;
    top_drag_accounts: {account_name: string; total: number; pct_of_cogs: number}[];
}

export interface XeroPnLData {
    meta: XeroPnLMeta;
    kpi_cards: XeroPnLKpiCard[];
    pnl_table: {
        months: VNextMonth[];
        section_totals: Record<string, Record<string, number>>;
        gross_profit: Record<string, number>;
        gross_margin_pct: Record<string, number | null>;
        net_profit: Record<string, number>;
        net_profit_pct: Record<string, number | null>;
        ebitda: Record<string, number>;
        ebitda_margin_pct: Record<string, number | null>;
        comparison: VNextComparisonBlock | null;
        row_variance?: Record<string, {delta: number; delta_pct: number | null}> | null;
    };
    cogs_build: XeroPnLCogsAccount[];
    opex_highlights: XeroPnLOpexHighlight[];
    monthly_performance: XeroPnLMonthlyPerf[];
    trend_monthly_performance?: XeroPnLMonthlyPerf[];
    margin_gap: XeroPnLMarginGap;
    cfo_questions: string[];
    checks_summary: VNextChecksSummary | null;
    report_coverage?: {
        months_covered: number;
        months_missing: string[];
    };
}

export function useVNextXeroPnL(
    workspaceId: string | null,
    scenarioId?: string,
    months = 6,
    actualMonths?: number,
    periodOpts?: PeriodOptions,
) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (actualMonths) params.set("actual_months", String(actualMonths));
    appendPeriodParams(params, periodOpts);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/xero-pnl/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<XeroPnLData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Balance Sheet Summary Types ──────────────────────────────

export interface BSSummaryKpiCard {
    key: string;
    label: string;
    value: number | null;
    delta: number | null;
    delta_pct: number | null;
    vs_label: string;
}

export interface BSSummarySection {
    key: string;
    label: string;
    rows: {node_id: string; label: string; values: Record<string, number>}[];
    total: Record<string, number>;
}

export interface BSSummaryRatio {
    key: string;
    label: string;
    value: number | null;
    calculation: string;
}

export interface BSSummaryTrend {
    month_key: string;
    label: string;
    status: "actual" | "forecast";
    total_assets: number;
    total_liabilities: number;
    total_equity: number;
    net_assets: number;
    working_capital: number;
}

export interface BSSummaryInsight {
    type: "insight" | "warning";
    title: string;
    text: string;
}

export interface BSSummaryData {
    meta: {
        workspace_name: string;
        scenario_name: string;
        period_label: string;
        generated_at: string;
    };
    kpi_cards: BSSummaryKpiCard[];
    bs_table: {
        periods: {month_key: string; label: string; status: string}[];
        sections: BSSummarySection[];
        net_assets: Record<string, number>;
        balance_check: Record<string, number>;
    };
    key_ratios: BSSummaryRatio[];
    monthly_trends: BSSummaryTrend[];
    composition: {
        assets: {category: string; value: number}[];
    };
    insights: BSSummaryInsight[];
    checks_summary: VNextChecksSummary | null;
}

export function useVNextBSSummary(
    workspaceId: string | null,
    scenarioId?: string,
    months = 6,
    actualMonths?: number,
    periodOpts?: PeriodOptions,
) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (actualMonths) params.set("actual_months", String(actualMonths));
    appendPeriodParams(params, periodOpts);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/xero-bs-summary/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<BSSummaryData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextOps(workspaceId: string | null, scenarioId?: string) {
    const params = new URLSearchParams();
    if (scenarioId) params.set("scenario_id", scenarioId);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/ops/?${params}` : null;

    const {data, error, isLoading, mutate} = useSWR<{scenario: VNextScenario; ops: VNextOp[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 10000},
    );

    return {
        ops: data?.ops ?? [],
        scenario: data?.scenario ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextBreakdown(workspaceId: string | null, scenarioId?: string, domain = "pnl", months = 6) {
    const params = new URLSearchParams({domain, months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/breakdown/?${params}` : null;

    const {data, error, isLoading, mutate} = useSWR<VNextBreakdownData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000},
    );

    return {
        data: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextAR(
    workspaceId: string | null,
    asOfDate?: string,
    periodFrom?: string,
    periodTo?: string,
) {
    const params = new URLSearchParams();
    if (asOfDate) params.set("as_of_date", asOfDate);
    if (periodFrom) params.set("period_from", periodFrom);
    if (periodTo) params.set("period_to", periodTo);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/ar/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<ARData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextRevenue(
    workspaceId: string | null,
    months = 12,
    periodOpts?: PeriodOptions,
) {
    const params = new URLSearchParams({months: String(months)});
    appendPeriodParams(params, periodOpts);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/revenue/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<RevenueData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextKPIs(
    workspaceId: string | null,
    scenarioId?: string,
    months = 12,
    periodOpts?: PeriodOptions,
) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    appendPeriodParams(params, periodOpts);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/kpis/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<KPIDashboardData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Mutations ──────────────────────────────────────────────────

export async function createMapping(data: {
    domain: string;
    source_type: string;
    source_id: string;
    source_label?: string;
    category_key: string;
    driver_type?: string;
    proposed_by?: string;
}): Promise<VNextCategoryMapping> {
    const result = await mutationFetch<{mapping: VNextCategoryMapping}>(
        "/api/model-vnext/mappings/",
        {method: "POST", body: data, errorLabel: "Failed to create mapping"},
    );
    return result.mapping;
}

export async function approveMapping(
    mappingId: string,
    action: "approve" | "reject" = "approve",
): Promise<VNextCategoryMapping> {
    const result = await mutationFetch<{mapping: VNextCategoryMapping}>(
        `/api/model-vnext/mappings/${mappingId}/approve/`,
        {method: "POST", body: {action}, errorLabel: `Failed to ${action} mapping`},
    );
    return result.mapping;
}

export async function createScenario(
    workspaceId: string,
    name: string,
    baselineMethod = "avg_6m",
): Promise<VNextScenario> {
    const result = await mutationFetch<{scenario: VNextScenario}>(
        `/api/model-vnext/workspace/${workspaceId}/scenarios/`,
        {method: "POST", body: {name, baseline_method: baselineMethod}, errorLabel: "Failed to create scenario"},
    );
    return result.scenario;
}

export async function deleteScenario(
    workspaceId: string,
    scenarioId: string,
): Promise<{deleted: string}> {
    return mutationFetch<{deleted: string}>(
        `/api/model-vnext/workspace/${workspaceId}/scenarios/`,
        {method: "DELETE", body: {scenario_id: scenarioId}, errorLabel: "Failed to delete scenario"},
    );
}

export async function previewOps(
    workspaceId: string,
    ops: VNextOpInput[],
    scenarioId?: string,
    months = 6,
    domain = "pnl",
    actualMonths?: number,
): Promise<VNextPreviewResult> {
    const body: Record<string, unknown> = {ops, scenario_id: scenarioId, months, domain};
    if (actualMonths) body.actual_months = actualMonths;
    return mutationFetch<VNextPreviewResult>(
        `/api/model-vnext/workspace/${workspaceId}/preview/`,
        {method: "POST", body, errorLabel: "Failed to preview ops"},
    );
}

export async function commitOps(
    workspaceId: string,
    ops: VNextOpInput[],
    scenarioId?: string,
    months = 6,
    domain = "pnl",
    createdBy: "human" | "agent" = "human",
    actualMonths?: number,
): Promise<{committed_ops: VNextOp[]; scenario: VNextScenario; months: VNextMonth[]}> {
    const body: Record<string, unknown> = {
        ops,
        scenario_id: scenarioId,
        months,
        domain,
        created_by: createdBy,
    };
    if (actualMonths) body.actual_months = actualMonths;
    return mutationFetch<{committed_ops: VNextOp[]; scenario: VNextScenario; months: VNextMonth[]}>(
        `/api/model-vnext/workspace/${workspaceId}/commit/`,
        {method: "POST", body, errorLabel: "Failed to commit ops"},
    );
}

export async function deleteOp(workspaceId: string, opId: string): Promise<VNextOp> {
    const result = await mutationFetch<{op: VNextOp}>(
        `/api/model-vnext/workspace/${workspaceId}/ops/${opId}/`,
        {method: "DELETE", errorLabel: "Failed to delete op"},
    );
    return result.op;
}

export async function purgeRevision(workspaceId: string, opId: string): Promise<{purged_op_id: string}> {
    return mutationFetch<{purged_op_id: string}>(
        `/api/model-vnext/workspace/${workspaceId}/revisions/${opId}/`,
        {method: "DELETE", errorLabel: "Failed to purge revision"},
    );
}

export async function patchScenario(
    workspaceId: string,
    data: {
        scenario_id?: string;
        baseline_method?: string;
        name?: string;
        assumptions?: Record<string, number | null>;
    },
): Promise<VNextScenario> {
    const result = await mutationFetch<{scenario: VNextScenario}>(
        `/api/model-vnext/workspace/${workspaceId}/scenarios/`,
        {method: "PATCH", body: data, errorLabel: "Failed to update scenario"},
    );
    return result.scenario;
}

export interface VNextCustomNode {
    id: string;
    label: string;
    category_key: string;
    domain: string;
    is_custom: boolean;
}

export function useVNextCustomNodes(workspaceId: string | null) {
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/nodes/custom/` : null;
    const {data, error, isLoading, mutate} = useSWR<{nodes: VNextCustomNode[]}>(url, apiFetcher);
    return {
        nodes: data?.nodes ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function createCustomNode(
    workspaceId: string,
    data: {label: string; category_key: string},
): Promise<VNextCustomNode> {
    const result = await mutationFetch<{node: VNextCustomNode}>(
        `/api/model-vnext/workspace/${workspaceId}/nodes/custom/`,
        {method: "POST", body: data, errorLabel: "Failed to create custom row"},
    );
    return result.node;
}

export async function deleteCustomNode(workspaceId: string, nodeId: string): Promise<void> {
    await mutationFetch<{deleted: string}>(
        `/api/model-vnext/workspace/${workspaceId}/nodes/custom/${nodeId}/`,
        {method: "DELETE", errorLabel: "Failed to delete custom row"},
    );
}

export async function fetchDrilldown(
    workspaceId: string,
    nodeId: string,
    monthKey: string,
    limit = 10,
): Promise<VNextDrilldownData> {
    const params = new URLSearchParams({node_id: nodeId, month_key: monthKey, limit: String(limit)});
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/drilldown/?${params}`, {credentials: "include"});
    if (!response.ok) throw new Error("Failed to fetch drilldown");
    const json = await response.json();
    return json.data;
}

export async function fetchVNextWorkbenchDrawerDrilldown(
    workspaceId: string,
    params: {
        nodeId: string;
        monthKey: string;
        level?: WorkbenchDrawerDrilldownLevel;
        contactId?: string;
        segment?: string;
        limit?: number;
        offset?: number;
    },
): Promise<WorkbenchDrawerDrilldownData> {
    const query = new URLSearchParams({
        node_id: params.nodeId,
        month_key: params.monthKey,
    });
    if (params.level) query.set("level", params.level);
    if (params.contactId) query.set("contact_id", params.contactId);
    if (params.segment) query.set("segment", params.segment);
    if (params.limit != null) query.set("limit", String(params.limit));
    if (params.offset != null) query.set("offset", String(params.offset));

    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/drawer-drilldown/?${query}`,
        {credentials: "include"},
    );
    if (!response.ok) throw new Error("Failed to fetch workbench drilldown");
    const json = await response.json();
    return json.data;
}

export async function fetchProvenance(
    workspaceId: string,
    nodeId: string,
    monthKey: string,
    scenarioId?: string,
): Promise<VNextProvenanceData> {
    const params = new URLSearchParams({node_id: nodeId, month_key: monthKey});
    if (scenarioId) params.set("scenario_id", scenarioId);
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/provenance/?${params}`, {credentials: "include"});
    if (!response.ok) throw new Error("Failed to fetch provenance");
    const json = await response.json();
    return json.data;
}

// ── Ledger Types ─────────────────────────────────────────────

export interface LedgerRow {
    line_id: string;
    date: string;
    description: string;
    account_code: string;
    account_name: string;
    contact_name: string;
    net_amount: number;
    source_type: string;
    reference: string;
    invoice_number?: string;
    invoice_type?: string;
    invoice_contact?: string;
    source_label?: string;
}

export interface SourceBreakdown {
    source_type: string;
    count: number;
    total: number;
}

export interface TopContact {
    contact_name: string;
    total_net: number;
    txn_count: number;
}

export interface LedgerResponse {
    rows: LedgerRow[];
    total_net: number;
    count: number;
    limit: number;
    offset: number;
    source_breakdown: SourceBreakdown[];
    top_contacts: TopContact[];
    sync_warning?: string;
}

export async function fetchLedgerRows(
    workspaceId: string,
    monthKey: string,
    accountCode: string,
    source: "pnl" | "cashflow",
    limit = 50,
    offset = 0,
    nodeId?: string,
    sourceType?: string,
): Promise<LedgerResponse> {
    const params = new URLSearchParams({
        month_key: monthKey,
        source,
        limit: String(limit),
        offset: String(offset),
    });
    if (accountCode) params.set("account_id", accountCode);
    if (nodeId) params.set("node_id", nodeId);
    if (sourceType) params.set("source_type", sourceType);
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/ledger/?${params}`, {credentials: "include"});
    if (!response.ok) throw new Error("Failed to fetch ledger rows");
    const json = await response.json();
    return json.data;
}

// ── Knowledge Document Types ──────────────────────────────────

export interface VNextKnowledgeDocument {
    id: string;
    filename: string;
    mime_type: string;
    size_bytes: number;
    sha256: string;
    extracted_chars: number;
    extraction_status: "pending" | "done" | "failed";
    extraction_warnings: string[];
    assumption_count: number | null;
    context_count?: number;
    dataset_count?: number;
    suggestion_count?: number;
    created_at: string;
    updated_at: string;
}

export interface VNextBusinessContextItem {
    id: string;
    workspace_id: string;
    scenario_id: string | null;
    month_key: string;
    period_start: string;
    period_end: string;
    scope_type: "company" | "segment" | "account" | "customer" | "product";
    scope_ref: string;
    context_type: "event" | "decision" | "risk" | "one_off" | "constraint" | "operational_driver";
    title: string;
    summary: string;
    impact_hint: string;
    direction: "up" | "down" | "neutral";
    magnitude_band: "small" | "medium" | "large";
    status: "draft" | "approved" | "rejected";
    source_document_id: string | null;
    source_document_filename: string | null;
    source_excerpt: string;
    created_by: "ai" | "user";
    approved_by: string | null;
    created_at: string;
    updated_at: string;
}

export interface VNextDocumentDetail extends VNextKnowledgeDocument {
    extracted_text: string;
    assumptions: VNextAssumptionCandidate[];
    datasets: {
        id: string;
        dataset_type: string;
        row_count: number;
        confidence: number;
        status: string;
        warnings: string[];
        schema: Record<string, unknown>;
    }[];
    metrics: {
        id: string;
        metric_key: string;
        label: string;
        value: number;
        month_key: string;
        meta: Record<string, unknown>;
    }[];
    suggestions: {
        id: string;
        suggestion_type: string;
        target_node_id: string;
        target_label: string;
        target_month: string;
        rationale: string;
        confidence: number;
        status: string;
        applied_op_ids?: string[];
    }[];
    context_items: VNextBusinessContextItem[];
}

export interface VNextAssumptionCandidate {
    id: string;
    source_document_id: string | null;
    source_document_filename: string | null;
    source_excerpt: string;
    tab_scope: "summary" | "pnl" | "cashflow" | "all";
    title: string;
    rationale: string;
    confidence: number;
    proposed_op_json: Record<string, unknown>;
    status: "proposed" | "approved" | "rejected" | "committed";
    reviewed_by: string | null;
    reviewed_at: string | null;
    committed_op_ids: string[];
    created_at: string;
    updated_at: string;
}

// ── AR Types ─────────────────────────────────────────────────

export interface ARAgingBucket {
    bucket: string;
    total_due: number;
    invoice_count: number;
    pct_of_total: number;
}

export interface ARDebtor {
    contact_id: string;
    contact_name: string;
    total_due: number;
    invoice_count: number;
}

export interface ARDSOData {
    dso: number;
    total_ar: number;
    trailing_90d_revenue: number;
}

export interface ARKPIs {
    total_ar: number;
    dso: number;
    overdue_total: number;
    overdue_pct: number;
    collection_rate: number;
    avg_invoice_value: number;
    invoice_count: number;
    weighted_avg_days_to_pay: number;
}

export interface ARTopCustomer {
    parent_name: string;
    total_due: number;
    invoice_count: number;
    overdue_amount: number;
    overdue_pct: number;
    segment: string;
}

export interface ARSegment {
    segment: string;
    total_due: number;
    invoice_count: number;
    overdue_amount: number;
    customer_count: number;
}

export interface ARWeeklyKeyWin {
    contact_name: string;
    amount: number;
    invoice_number: string;
}

export interface ARWeekly {
    week_key: string;
    invoiced_amount: number;
    invoice_count: number;
    paid_amount: number;
    key_wins: ARWeeklyKeyWin[];
}

export interface ARPastDue {
    contact_name: string;
    invoice_number: string;
    amount_due: number;
    due_date: string;
    days_overdue: number;
    original_amount: number;
}

export interface ARMonthlyTrend {
    month: string;
    total_invoiced: number;
    total_collected: number;
    collection_rate_pct: number;
    opening_ar: number;
    closing_ar: number;
}

export interface ARConcentration {
    hhi: number;
    top_1_pct: number;
    top_5_pct: number;
    top_10_pct: number;
}

export interface ARData {
    aging: ARAgingBucket[];
    top_debtors: ARDebtor[];
    dso: ARDSOData | null;
    as_of_date: string;
    kpis: ARKPIs;
    top_customers: ARTopCustomer[];
    segment_breakdown: ARSegment[];
    weekly_invoicing: ARWeekly[];
    past_due: ARPastDue[];
    monthly_collection_trend: ARMonthlyTrend[];
    concentration: ARConcentration;
}

// ── Revenue Types ────────────────────────────────────────────

export interface RevenueCustomer {
    contact_id: string;
    contact_name: string;
    total_revenue: number;
    txn_count: number;
}

export interface RevenueMonthlyTrend {
    month_key: string;
    total_revenue: number;
}

export interface NewVsRepeat {
    month: string;
    new_count: number;
    repeat_count: number;
    new_revenue: number;
    repeat_revenue: number;
}

export interface RevenueKPIs {
    total_revenue: number;
    avg_monthly_revenue: number;
    mom_growth_pct: number | null;
    customer_count: number;
}

export interface RevenueData {
    top_customers: RevenueCustomer[];
    monthly_trend: RevenueMonthlyTrend[];
    new_vs_repeat: NewVsRepeat | null;
    kpis: RevenueKPIs;
    months: number;
}

// ── KPI Dashboard Types ──────────────────────────────────────

export interface KPIProfitability {
    gross_margin_pct: number;
    net_margin_pct: number;
    actual_net_income: number;
    forecast_net_income: number;
}

export interface KPIGrowth {
    revenue_mom_pct: number | null;
    actual_revenue: number;
    forecast_revenue: number;
    actual_months: number;
    forecast_months: number;
}

export interface KPIEfficiency {
    avg_revenue_per_month: number;
    avg_cost_per_month: number;
    cost_ratio: number;
}

export interface KPITrend {
    month_key: string;
    status: string;
    // null for actual months with no Xero P&L snapshot — render as a gap, never
    // substitute an engine-derived value.
    revenue: number | null;
    costs: number | null;
    net: number | null;
}

export interface KPIDashboardData {
    scenario: VNextScenario;
    kpis: {
        profitability: KPIProfitability;
        growth: KPIGrowth;
        efficiency: KPIEfficiency;
        ar?: {dso: number; total_ar: number};
    };
    trend: KPITrend[];
    n_actual_months: number;
    n_forecast_months: number;
}

export interface VNextTabExecutiveSummary {
    id: string;
    tab_key: "ceo_summary" | "summary" | "pnl" | "cashflow" | "bs" | "pricing" | "health";
    content_markdown: string;
    status: "fresh" | "stale" | "generating" | "failed";
    stale_reasons: string[];
    inputs_hash: string;
    generated_at: string | null;
    generated_by: "human" | "agent" | "system";
}

export interface ThreeStatementStatementSummaryMetric {
    key: string;
    label: string;
    value: number;
    format?: "currency" | "percent" | "number";
    delta?: number | null;
}

export interface ThreeStatementStatementData {
    months: VNextMonth[];
    totals?: Record<string, number>;
    summary_metrics?: ThreeStatementStatementSummaryMetric[];
}

export interface ThreeStatementScheduleData {
    key: string;
    label: string;
    unit?: string | null;
    months?: Record<string, number>;
    rows?: Array<Record<string, unknown>>;
}

export interface ThreeStatementForecastMeta {
    scenario: VNextScenario;
    as_of: string;
    engine_version?: string | null;
    n_actual_months: number;
    n_forecast_months: number;
    actual_range_label?: string | null;
    forecast_range_label?: string | null;
    sync_timestamps?: Record<string, string | null>;
}

export interface ThreeStatementForecastData {
    meta: ThreeStatementForecastMeta;
    pnl: ThreeStatementStatementData;
    bs: ThreeStatementStatementData;
    cf: ThreeStatementStatementData;
    schedules: Record<string, ThreeStatementScheduleData>;
    checks: ThreeStatementCheck[];
    warnings: ThreeStatementWarning[];
    cell_provenance?: Record<string, ThreeStatementCellProvenance>;
}

export interface DataFreshness {
    last_snapshot_sync: string | null;
    last_journal_sync: string | null;
    last_model_rebuild: string | null;
    snapshot_months: number;
    journal_months: number;
    status: "fresh" | "stale" | "partial" | "error" | "unknown";
}

export interface ModelingWorkbenchMonth {
    month_key: string;
    label: string;
    status: "actual" | "forecast";
    source?: string;
}

export interface ModelingWorkbenchKpi {
    key: string;
    label: string;
    value: number | null;
    baseline?: number | null;
    format: "currency" | "percent" | "months";
    period: string;
}

export interface ModelingWorkbenchCellValue {
    value: number;
    status: "actual" | "forecast";
    has_ops: boolean;
    is_override: boolean;
    linked_from?: "pnl" | "cf" | null;
}

export interface ModelingWorkbenchRow {
    node_id: string;
    label: string;
    category_key: string;
    depth: number;
    row_type: "section_header" | "section_total" | "account" | "computed" | "synthetic" | "line_item" | "variance";
    is_expandable: boolean;
    expand_level: string | null;
    inline_level?: "parent_group" | "account_group" | "account" | "segment" | "customer_parent" | "customer_child" | "supplier";
    segment?: string;
    parent_contact_id?: string;
    contact_id?: string;
    source_account_id?: string;
    xero_account_id?: string;
    parent_group?: string;
    account_group?: string;
    is_bold: boolean;
    values: Record<string, ModelingWorkbenchCellValue>;
    total: number;
    assumption_summary?: string | null;
}

export interface ModelingWorkbenchStatement {
    key: "pnl" | "bs" | "cf";
    label: string;
    months: ModelingWorkbenchMonth[];
    rows: ModelingWorkbenchRow[];
}

export interface ModelingWorkbenchCellDetail {
    node_id: string;
    month_key: string;
    statement: "pnl" | "bs" | "cf";
    value: number | null;
    baseline: number | null;
    is_override: boolean;
    ops_applied: Record<string, unknown>[];
    calculation: string;
    method: string;
    role: string;
    linked_from?: string | null;
    is_editable: boolean;
}

export interface ModelingWorkbenchAssumption {
    statement: "pnl" | "bs" | "cf";
    node_id: string;
    node_label?: string;
    month_key: string;
    value: number | null;
    baseline: number | null;
    is_override: boolean;
    ops_applied: Record<string, unknown>[];
    calculation: string;
}

export interface ModelingWorkbenchOpHistoryItem {
    id: string;
    op_type: string;
    target_node_id: string | null;
    target_month: string | null;
    label: string;
    params: Record<string, unknown>;
    is_active: boolean;
}

export interface ModelingWorkbenchMeta {
    scenario: VNextScenario;
    n_actual_months: number;
    n_forecast_months: number;
    as_of: string;
    engine_version?: string | null;
    data_freshness?: DataFreshness;
}

export interface ModelingWorkbenchKpiPeriods {
    options: {key: string; label: string}[];
    kpis_by_period: Record<string, ModelingWorkbenchKpi[]>;
}

export interface ModelingWorkbenchData {
    meta: ModelingWorkbenchMeta;
    kpis: ModelingWorkbenchKpi[];
    kpi_periods?: ModelingWorkbenchKpiPeriods;
    statements: {
        pnl: ModelingWorkbenchStatement;
        bs: ModelingWorkbenchStatement;
        cf: ModelingWorkbenchStatement;
    };
    checks: ThreeStatementCheck[];
    warnings: ThreeStatementWarning[];
    ops_history: ModelingWorkbenchOpHistoryItem[];
    revenue_tie_out?: RevenueTieOutData | null;
}

export interface RevenueTieOutCell {
    account_value: number;
    bottom_up: number;
    variance: number;
    status: "green" | "amber" | "red" | "ledger_only";
    note?: string;
}

export interface RevenueTieOutRow {
    account_node_id: string;
    label: string;
    account_code: string;
    in_grid: boolean;
    months: Record<string, RevenueTieOutCell>;
    total_variance: number;
}

export interface RevenueTieOutData {
    months: {month_key: string; status: string}[];
    rows: RevenueTieOutRow[];
}

// ── Reconciliation Types ─────────────────────────────────────

export interface VNextReconciliationResultItem {
    id: string;
    period: string;
    level: "section" | "account";
    section: string;
    account_id: string;
    account_name: string;
    model_amount: number;
    xero_amount: number;
    delta: number;
    delta_pct: number | null;
    variance_type: "match" | "amount_diff" | "reclassification" | "missing_in_model" | "missing_in_xero" | "timing";
    status: "PASS" | "WARN" | "FAIL";
    message: string;
    known_variance_id: string | null;
    is_suppressed: boolean;
}

export interface VNextReconciliationRunData {
    id: string;
    overall_status: "PASS" | "WARN" | "FAIL";
    total_variances: number;
    new_variances: number;
    known_variances_suppressed: number;
    months_checked: string[];
    triggered_by: string;
    started_at: string | null;
    completed_at: string | null;
}

export interface VNextKnownVarianceData {
    id: string;
    section: string;
    account_id: string;
    account_name: string;
    variance_type: string;
    description: string;
    max_amount: number;
    status: "active" | "expired" | "revoked";
    valid_from: string;
    valid_until: string;
    created_at: string;
}

// ── Check Types ──────────────────────────────────────────────

export interface VNextCheckResult {
    check_id: string;
    tab: string;
    period: string;
    actual: number;
    expected: number;
    delta: number;
    delta_pct: number | null;
    status: "PASS" | "WARN" | "FAIL";
    severity: "INFO" | "WARN" | "ERROR";
    message: string;
}

export interface VNextCheckRun {
    id: string;
    run_type: string;
    tab_scope: string;
    status: string;
    checks_passed: number;
    checks_warned: number;
    checks_failed: number;
    started_at: string;
    completed_at: string | null;
    triggered_by: string;
    results: VNextCheckResult[];
}

export interface VNextChecksSummary {
    passed: number;
    warned: number;
    failed: number;
    results: VNextCheckResult[];
    /** ISO timestamp of the check run these results came from; null if never run. */
    run_at?: string | null;
}

// ── Check Hooks ─────────────────────────────────────────────

export function useVNextChecks(workspaceId: string | null) {
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/checks/` : null;

    const {data, error, isLoading, mutate} = useSWR<{run: VNextCheckRun | null}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );

    return {
        run: data?.run ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function runChecks(
    workspaceId: string,
    opts?: {tab_scope?: string; check_ids?: string[]; include_api_checks?: boolean},
): Promise<{run_id: string; checks_passed: number; checks_warned: number; checks_failed: number; results: VNextCheckResult[]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/checks/run/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify({include_api_checks: true, ...(opts ?? {})}),
    });
    if (!response.ok) throw new Error("Failed to run checks");
    const json = await response.json();
    return json.data;
}

// ── Sync Status ──────────────────────────────────────────────

export interface SyncStatusData {
    has_synced: boolean;
    status?: string;
    sync_type?: string;
    entity_type?: string;
    records_synced?: number;
    records_failed?: number;
    pages_fetched?: number;
    is_complete?: boolean;
    last_journal_offset?: number;
    error_detail?: string | null;
    has_incomplete_full?: boolean;
    started_at?: string;
    completed_at?: string;
    is_stale?: boolean;
    snapshot_ready?: boolean;
}

export function useVNextSyncStatus(activePolling = false) {
    const {data, error, isLoading, mutate} = useSWR<SyncStatusData>(
        "/api/model-vnext/sync-status/",
        apiFetcher,
        {refreshInterval: activePolling ? 3000 : 30000},
    );
    return {syncStatus: data ?? null, error: error ? friendlyErrorMessage(error) : null, isLoading, refresh: mutate};
}

// ── Granular Data Sync ────────────────────────────────────────

export interface EntitySyncStatus {
    entity_type: string;
    status: string | null;       // running | success | partial | error | cancelled | null
    records_synced: number;
    record_count: number;        // actual DB row count
    started_at: string | null;
    completed_at: string | null;
    error_detail: string | null;
    is_complete: boolean | null;
    pages_fetched: number | null;
    needs_continuation: boolean; // true if last sync was partial/incomplete
}

export interface SyncStatusAllData {
    entities: Record<string, EntitySyncStatus>;
    overall_health: "healthy" | "stale" | "incomplete" | "never_synced";
    chain_running: boolean;      // true if sync-all chain is active
}

export function useVNextSyncStatusAll(activePolling = false) {
    const anyRunning = activePolling;
    const {data, error, isLoading, mutate} = useSWR<SyncStatusAllData>(
        "/api/model-vnext/sync-status-all/",
        apiFetcher,
        {refreshInterval: anyRunning ? 3000 : 30000},
    );
    return {
        syncStatusAll: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function triggerEntitySync(entityType: string, lookbackMonths?: number): Promise<{triggered: boolean; task_id?: string}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const body: Record<string, unknown> = {entity_type: entityType};
    if (lookbackMonths != null) body.lookback_months = lookbackMonths;
    const response = await fetch("/api/model-vnext/sync-entity/", {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || `Sync trigger failed (${response.status})`);
    }
    const json = await response.json();
    return json.data;
}

export async function triggerSyncAll(workspaceId?: string): Promise<{triggered: boolean}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const body: Record<string, string> = {};
    if (workspaceId) body.workspace_id = workspaceId;
    const response = await fetch("/api/model-vnext/sync-all/", {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || `Sync-all trigger failed (${response.status})`);
    }
    const json = await response.json();
    return json.data;
}

export async function stopEntitySync(entityType: string): Promise<{cancelled: number}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch("/api/model-vnext/sync-stop/", {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify({entity_type: entityType}),
    });
    if (!response.ok) throw new Error("Stop sync failed");
    const json = await response.json();
    return json.data;
}

export async function clearEntitySync(entityType: string): Promise<{cleared: number}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch("/api/model-vnext/sync-clear/", {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify({entity_type: entityType}),
    });
    if (!response.ok) throw new Error("Clear sync failed");
    const json = await response.json();
    return json.data;
}

export async function runPostSyncHook(hook: string, workspaceId?: string): Promise<Record<string, unknown>> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch("/api/model-vnext/sync-post-hook/", {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify({hook, workspace_id: workspaceId}),
    });
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || `Hook failed (${response.status})`);
    }
    const json = await response.json();
    return json.data;
}

// ── Seed ──────────────────────────────────────────────────────

export interface VNextSeedResult {
    nodes_created: number;
    series_values_created: number;
    edges_created: number;
    warnings: string[];
    sync?: {records_synced: number; records_failed: number} | null;
}

export async function seedWorkspace(
    workspaceId: string,
    tenantId: string,
    months?: number,
): Promise<VNextSeedResult> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/seed/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify({tenant_id: tenantId, months: months ?? 24}),
    });
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        const detail = json?.errors?.[0] || json?.error || json?.detail || `Seed failed (${response.status})`;
        throw new Error(detail);
    }
    const json = await response.json();
    return {...json.data, warnings: json.warnings ?? []};
}

// ── Knowledge Hooks ──────────────────────────────────────────

export function useVNextKnowledge(workspaceId: string | null) {
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/knowledge/` : null;

    const {data, error, isLoading, mutate} = useSWR<{documents: VNextKnowledgeDocument[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 10000},
    );

    return {
        documents: data?.documents ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextAssumptions(workspaceId: string | null, status?: string, tabScope?: string) {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (tabScope) params.set("tab_scope", tabScope);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/assumptions/?${params}` : null;

    const {data, error, isLoading, mutate} = useSWR<{assumptions: VNextAssumptionCandidate[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 10000},
    );

    return {
        assumptions: data?.assumptions ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextTabSummary(workspaceId: string | null, scenarioId?: string, tabKey = "summary") {
    const params = new URLSearchParams({tab_key: tabKey});
    if (scenarioId) params.set("scenario_id", scenarioId);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/tab-summary/?${params}` : null;

    const {data, error, isLoading, mutate} = useSWR<{summary: VNextTabExecutiveSummary | null}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );

    return {
        summary: data?.summary ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Knowledge Mutations ──────────────────────────────────────

export async function uploadKnowledgeDoc(
    workspaceId: string,
    file: File,
): Promise<{document: VNextKnowledgeDocument; warnings: string[]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/knowledge/upload/`, {
        method: "POST",
        headers: {"X-CSRFToken": csrfToken},
        credentials: "include",
        body: formData,
    });
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || `Upload failed (${response.status})`);
    }
    const json = await response.json();
    return {document: json.data.document, warnings: json.warnings ?? []};
}

export async function deleteKnowledgeDoc(
    workspaceId: string,
    docId: string,
): Promise<void> {
    await mutationFetch<unknown>(
        `/api/model-vnext/workspace/${workspaceId}/knowledge/${docId}/`,
        {method: "DELETE", errorLabel: "Failed to delete document"},
    );
}

export async function extractAssumptions(
    workspaceId: string,
    docId: string,
): Promise<{assumptions: VNextAssumptionCandidate[]; count: number}> {
    return mutationFetch<{assumptions: VNextAssumptionCandidate[]; count: number}>(
        `/api/model-vnext/workspace/${workspaceId}/knowledge/${docId}/extract-assumptions/`,
        {method: "POST", errorLabel: "Extraction failed"},
    );
}

export async function approveAssumption(
    workspaceId: string,
    assumptionId: string,
): Promise<VNextAssumptionCandidate> {
    const result = await mutationFetch<{assumption: VNextAssumptionCandidate}>(
        `/api/model-vnext/workspace/${workspaceId}/assumptions/${assumptionId}/approve/`,
        {method: "POST", errorLabel: "Failed to approve assumption"},
    );
    return result.assumption;
}

export async function rejectAssumption(
    workspaceId: string,
    assumptionId: string,
): Promise<VNextAssumptionCandidate> {
    const result = await mutationFetch<{assumption: VNextAssumptionCandidate}>(
        `/api/model-vnext/workspace/${workspaceId}/assumptions/${assumptionId}/reject/`,
        {method: "POST", errorLabel: "Failed to reject assumption"},
    );
    return result.assumption;
}

export async function commitAssumption(
    workspaceId: string,
    assumptionId: string,
): Promise<{assumption: VNextAssumptionCandidate; op: VNextOp}> {
    return mutationFetch<{assumption: VNextAssumptionCandidate; op: VNextOp}>(
        `/api/model-vnext/workspace/${workspaceId}/assumptions/${assumptionId}/commit/`,
        {method: "POST", errorLabel: "Failed to commit assumption"},
    );
}

export async function approveAndApplyAssumption(
    workspaceId: string,
    assumptionId: string,
    currentStatus: string,
): Promise<{assumption: VNextAssumptionCandidate; op: VNextOp}> {
    if (currentStatus === "proposed") {
        await approveAssumption(workspaceId, assumptionId);
    }
    const result = await commitAssumption(workspaceId, assumptionId);
    window.dispatchEvent(new CustomEvent("vnext-data-updated"));
    return result;
}

// ── Business Context Hooks ───────────────────────────────────

export function useVNextBusinessContext(workspaceId: string | null, status?: string) {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/business-context/?${params}` : null;

    const {data, error, isLoading, mutate} = useSWR<{context_items: VNextBusinessContextItem[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 10000},
    );

    return {
        contextItems: data?.context_items ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextDocumentDetail(workspaceId: string | null, docId: string | null) {
    const url = workspaceId && docId
        ? `/api/model-vnext/workspace/${workspaceId}/knowledge/${docId}/`
        : null;

    const {data, error, isLoading, mutate} = useSWR<{document: VNextDocumentDetail}>(
        url,
        apiFetcher,
        {dedupingInterval: 10000},
    );

    return {
        document: data?.document ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Business Context Mutations ──────────────────────────────

export async function approveBusinessContextItem(
    workspaceId: string,
    itemId: string,
): Promise<VNextBusinessContextItem> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/business-context/${itemId}/approve/`,
        {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
        },
    );
    if (!response.ok) throw new Error("Failed to approve context item");
    const json = await response.json();
    return json.data.context_item;
}

export async function rejectBusinessContextItem(
    workspaceId: string,
    itemId: string,
): Promise<VNextBusinessContextItem> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/business-context/${itemId}/reject/`,
        {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
        },
    );
    if (!response.ok) throw new Error("Failed to reject context item");
    const json = await response.json();
    return json.data.context_item;
}

export async function deleteBusinessContextItem(
    workspaceId: string,
    itemId: string,
): Promise<void> {
    await mutationFetch<unknown>(
        `/api/model-vnext/workspace/${workspaceId}/business-context/${itemId}/`,
        {method: "DELETE", errorLabel: "Failed to delete context item"},
    );
}

export async function createBusinessContextFromAssumption(
    workspaceId: string,
    assumptionId: string,
    overrides?: {context_type?: string; scope_type?: string; impact_hint?: string; direction?: string},
): Promise<{context_item: VNextBusinessContextItem; assumption: VNextAssumptionCandidate}> {
    return mutationFetch<{context_item: VNextBusinessContextItem; assumption: VNextAssumptionCandidate}>(
        `/api/model-vnext/workspace/${workspaceId}/business-context/from-assumption/${assumptionId}/`,
        {method: "POST", body: overrides ?? {}, errorLabel: "Failed to create context from assumption"},
    );
}

// ── Variance Types ───────────────────────────────────────────

export interface VarianceMonthCell {
    actual: number;
    forecast: number;
    variance: number;
    variance_pct: number;
}

export interface VarianceRow {
    node_id: string;
    line_item: string;
    category_key: string;
    months: Record<string, VarianceMonthCell>;
    total_actual: number;
    total_forecast: number;
    total_variance: number;
    total_variance_pct: number;
    status: "favorable" | "unfavorable" | "neutral";
}

export interface VarianceSummary {
    favorable: number;
    unfavorable: number;
    net: number;
    row_count: number;
}

export interface VarianceData {
    summary: VarianceSummary;
    rows: VarianceRow[];
    top_movers: VarianceRow[];
    month_keys: string[];
}

// ── Scenarios Compare Types ──────────────────────────────────

export interface ScenarioKPIs {
    revenue: number;
    costs: number;
    gross_profit: number;
    net_income: number;
}

export interface ScenarioComparisonEntry {
    id: string;
    name: string;
    is_default: boolean;
    baseline_method: string;
    kpis: ScenarioKPIs;
    deltas_vs_base: ScenarioKPIs | null;
}

export interface ScenariosCompareData {
    base_scenario_id: string | null;
    scenarios: ScenarioComparisonEntry[];
    months: number;
}

// ── Variance & Scenarios Hooks ───────────────────────────────

export function useVNextVariance(workspaceId: string | null, scenarioId?: string, months = 3) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/variance/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<VarianceData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextScenariosCompare(workspaceId: string | null, months = 12) {
    const params = new URLSearchParams({months: String(months)});
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/scenarios-compare/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<ScenariosCompareData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function refreshTabSummary(
    workspaceId: string,
    scenarioId: string,
    tabKey: string,
    periodMonths?: number,
    currentMonth?: string,
): Promise<VNextTabExecutiveSummary> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const body: Record<string, unknown> = {tab_key: tabKey, scenario_id: scenarioId};
    if (periodMonths) body.period_months = periodMonths;
    if (currentMonth) body.current_month = currentMonth;
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/tab-summary/refresh/`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify(body),
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to refresh summary");
    }
    const json = await response.json();
    return json.data.summary;
}

// ── Reconciliation Hooks ─────────────────────────────────────

export function useVNextReconciliation(workspaceId: string | null) {
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/reconciliation/latest/` : null;

    const {data, error, isLoading, mutate} = useSWR<{run: VNextReconciliationRunData | null; results: VNextReconciliationResultItem[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );

    return {
        run: data?.run ?? null,
        results: data?.results ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNext3StatementForecast(
    workspaceId: string | null,
    scenarioId?: string,
    months = 6,
    actualMonths = 12,
) {
    const params = new URLSearchParams();
    if (scenarioId) params.set("scenario_id", scenarioId);
    params.set("n_months", String(months));
    params.set("n_actual_months", String(actualMonths));

    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/3statement-forecast/?${params.toString()}`
        : null;

    const {data, error, isLoading, mutate} = useSWR<ThreeStatementForecastData>(
        url,
        apiFetcher,
        {dedupingInterval: 15000},
    );

    return {
        data: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextModelingWorkbench(
    workspaceId: string | null,
    scenarioId?: string,
    months = 6,
    actualMonths = 12,
) {
    const params = new URLSearchParams();
    if (scenarioId) params.set("scenario_id", scenarioId);
    params.set("n_months", String(months));
    params.set("n_actual_months", String(actualMonths));

    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/workbench/?${params.toString()}`
        : null;

    const {data, error, isLoading, mutate} = useSWR<ModelingWorkbenchData>(
        url,
        apiFetcher,
        {dedupingInterval: 15000},
    );

    return {
        data: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextModelingWorkbenchCellDetail(
    workspaceId: string | null,
    statement: "pnl" | "bs" | "cf" | null,
    nodeId: string | null,
    monthKey: string | null,
    scenarioId?: string,
    months = 6,
    actualMonths = 12,
) {
    const params = new URLSearchParams();
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (statement) params.set("statement", statement);
    if (nodeId) params.set("node_id", nodeId);
    if (monthKey) params.set("month_key", monthKey);
    params.set("n_months", String(months));
    params.set("n_actual_months", String(actualMonths));

    const url = workspaceId && statement && nodeId && monthKey
        ? `/api/model-vnext/workspace/${workspaceId}/workbench/cell-detail/?${params.toString()}`
        : null;

    const {data, error, isLoading, mutate} = useSWR<ModelingWorkbenchCellDetail>(
        url,
        apiFetcher,
        {dedupingInterval: 5000},
    );

    return {
        data: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextModelingWorkbenchAssumptions(
    workspaceId: string | null,
    scenarioId?: string,
    months = 6,
    actualMonths = 12,
    enabled = true,
) {
    const params = new URLSearchParams();
    if (scenarioId) params.set("scenario_id", scenarioId);
    params.set("n_months", String(months));
    params.set("n_actual_months", String(actualMonths));

    const url = workspaceId && enabled
        ? `/api/model-vnext/workspace/${workspaceId}/workbench/assumptions/?${params.toString()}`
        : null;

    const {data, error, isLoading, mutate} = useSWR<{assumptions: ModelingWorkbenchAssumption[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 15000},
    );

    return {
        assumptions: data?.assumptions ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export interface ExpandReconciliationEntry {
    snapshot_total: number;
    account_total: number;
    variance: number;
    status: string;
}

export interface ExpandResult {
    rows: ModelingWorkbenchRow[];
    reconciliation?: Record<string, ExpandReconciliationEntry>;
}

interface ModelingWorkbenchExpandOptions {
    level?: string;
    parentGroup?: string;
    accountGroup?: string;
    accountId?: string;
    accountNodeId?: string;
    segment?: string;
    parentContactId?: string;
}

export async function fetchVNextModelingWorkbenchExpand(
    workspaceId: string,
    statement: "pnl" | "bs" | "cf",
    sectionKey: string,
    scenarioId?: string,
    months = 6,
    actualMonths = 12,
    options: ModelingWorkbenchExpandOptions = {},
): Promise<ExpandResult> {
    const params = new URLSearchParams();
    if (scenarioId) params.set("scenario_id", scenarioId);
    params.set("statement", statement);
    params.set("section_key", sectionKey);
    params.set("n_months", String(months));
    params.set("n_actual_months", String(actualMonths));
    if (options.level) params.set("level", options.level);
    if (options.parentGroup) params.set("parent_group", options.parentGroup);
    if (options.accountGroup) params.set("account_group", options.accountGroup);
    if (options.accountId) params.set("account_id", options.accountId);
    if (options.accountNodeId) params.set("account_node_id", options.accountNodeId);
    if (options.segment) params.set("segment", options.segment);
    if (options.parentContactId) params.set("parent_contact_id", options.parentContactId);

    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/workbench/expand/?${params.toString()}`,
        {credentials: "include"},
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to expand workbench section");
    }
    const json = await response.json();
    return {
        rows: (json.data?.rows ?? []) as ModelingWorkbenchRow[],
        reconciliation: json.data?.reconciliation,
    };
}

export async function commitVNextModelingWorkbenchEdit(
    workspaceId: string,
    data: {
        scenario_id?: string;
        ops: VNextOpInput[];
        n_months?: number;
        n_actual_months?: number;
        created_by?: "human" | "agent";
    },
): Promise<ModelingWorkbenchData> {
    return mutationFetch<ModelingWorkbenchData>(
        `/api/model-vnext/workspace/${workspaceId}/workbench/edit/`,
        {method: "POST", body: data, errorLabel: "Failed to commit workbench edit"},
    );
}

export type WorkbenchBottomUpOverrideScope =
    | {
        kind: "revenue";
        scope_type: RevenueScopeType;
        scope_id: string;
        scope_label?: string;
        account_id?: string;
    }
    | {
        kind: "cost";
        pnl_section: "cogs" | "expenses";
        scope_type: CostScopeType;
        scope_id: string;
        scope_label?: string;
        account_id?: string;
    };

export async function commitVNextModelingWorkbenchBottomUpOverride(
    workspaceId: string,
    data: {
        scenario_id?: string;
        scope: WorkbenchBottomUpOverrideScope;
        month_key: string;
        value?: number | null;
        note?: string;
        clear?: boolean;
        n_months?: number;
        n_actual_months?: number;
    },
): Promise<ModelingWorkbenchData> {
    return mutationFetch<ModelingWorkbenchData>(
        `/api/model-vnext/workspace/${workspaceId}/workbench/bottom-up-override/`,
        {method: "POST", body: data, errorLabel: "Failed to commit bottom-up override"},
    );
}

export async function revertVNextModelingWorkbenchOps(
    workspaceId: string,
    data: {
        scenario_id?: string;
        op_ids?: string[];
        count?: number;
        n_months?: number;
        n_actual_months?: number;
    },
): Promise<ModelingWorkbenchData> {
    return mutationFetch<ModelingWorkbenchData>(
        `/api/model-vnext/workspace/${workspaceId}/workbench/revert/`,
        {method: "POST", body: data, errorLabel: "Failed to revert workbench revision"},
    );
}

export async function runReconciliation(
    workspaceId: string,
    months?: string[],
): Promise<{run_id: string; overall_status: string; results: VNextReconciliationResultItem[]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const body: Record<string, unknown> = {};
    if (months) body.months = months;
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/reconciliation/run/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error("Failed to run reconciliation");
    const json = await response.json();
    return json.data;
}

export function useVNextKnownVariances(workspaceId: string | null, varianceStatus = "active") {
    const params = new URLSearchParams({status: varianceStatus});
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/known-variances/?${params}` : null;

    const {data, error, isLoading, mutate} = useSWR<{known_variances: VNextKnownVarianceData[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );

    return {
        knownVariances: data?.known_variances ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function acknowledgeVariance(
    workspaceId: string,
    data: {
        section: string;
        account_id?: string;
        account_name?: string;
        variance_type: string;
        description?: string;
        max_amount?: number;
        valid_from?: string;
        valid_until?: string;
    },
): Promise<VNextKnownVarianceData> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/known-variances/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error("Failed to acknowledge variance");
    const json = await response.json();
    return json.data.known_variance;
}

// ── Pricing Types ────────────────────────────────────────────

export interface PricingProduct {
    product_key: string;
    description: string;
    account_code: string;
    total_revenue: number;
    total_quantity: number;
    avg_unit_price: number;
    min_unit_price: number;
    max_unit_price: number;
    invoice_count: number;
    pct_of_revenue: number;
}

export interface PricingCustomer {
    contact_name: string;
    total_revenue: number;
    avg_unit_price: number;
    invoice_count: number;
    price_vs_avg_pct: number;
}

export interface PricingMargin {
    category_key: string;
    label: string;
    revenue: number;
    cogs: number;
    margin: number;
    margin_pct: number;
}

export interface PricingMonthlyTrend {
    month_key: string;
    total_revenue: number;
    avg_unit_price: number;
    total_quantity: number;
}

export interface PricingKnowledgeDoc {
    id: string;
    filename: string;
    has_pricing_context: boolean;
}

export interface PricingKPIs {
    total_revenue: number;
    unique_products: number;
    avg_price_all: number;
    top_product_concentration: number;
}

export interface PricingData {
    products: PricingProduct[];
    customer_pricing: PricingCustomer[];
    margin_analysis: PricingMargin[];
    monthly_trend: PricingMonthlyTrend[];
    kpis: PricingKPIs;
    knowledge_docs: PricingKnowledgeDoc[];
}

// ── Health Check Types ───────────────────────────────────────

export interface HealthCheckItem {
    check_id: string;
    status: "PASS" | "WARN" | "FAIL";
    severity: "INFO" | "WARN" | "ERROR";
    message: string;
    actual: number;
    expected: number;
    delta: number;
}

export interface HealthCheckCategory {
    label: string;
    checks: HealthCheckItem[];
}

export interface HealthCheckSummary {
    total_checks: number;
    passed: number;
    warnings: number;
    failures: number;
    score: number;
}

export interface HealthCheckData {
    run_id: string;
    started_at: string | null;
    completed_at: string | null;
    summary: HealthCheckSummary;
    categories: Record<string, HealthCheckCategory>;
}

// ── Pricing Hook ─────────────────────────────────────────────

export function useVNextPricing(workspaceId: string | null, months = 12) {
    const params = new URLSearchParams({months: String(months)});
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/pricing/?${params}` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<PricingData>(
        url,
        apiFetcher,
        {dedupingInterval: 60000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Health Check Hooks ───────────────────────────────────────

export function useVNextHealthCheck(workspaceId: string | null) {
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/health-check/` : null;

    const {data, error, isLoading, mutate} = useSWR<HealthCheckData | {run: null}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );

    // Backend returns {run: null} if no run yet, or the full HealthCheckData
    const runData = data && "run_id" in data ? (data as HealthCheckData) : null;

    return {
        data: runData,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function runHealthCheck(
    workspaceId: string,
): Promise<HealthCheckData> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/health-check/run/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify({}),
    });
    if (!response.ok) throw new Error("Failed to run health check");
    const json = await response.json();
    return json.data;
}

export async function revokeKnownVariance(
    workspaceId: string,
    kvId: string,
): Promise<VNextKnownVarianceData> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/known-variances/${kvId}/`, {
        method: "PATCH",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify({status: "revoked"}),
    });
    if (!response.ok) throw new Error("Failed to revoke known variance");
    const json = await response.json();
    return json.data.known_variance;
}

// ── Insights Types ───────────────────────────────────────────

export interface KnowledgeDataset {
    id: string;
    doc_id: string;
    doc_filename: string;
    dataset_type: "revenue" | "payroll" | "rate_card" | "booking" | "general";
    schema: {columns?: {name: string; dtype: string; sample_values: string[]}[]};
    row_count: number;
    confidence: number;
    status: "parsed" | "reviewed" | "failed";
    warnings: string[];
}

export interface KnowledgeMetricKPI {
    metric_key: string;
    label: string;
    value: number;
    meta: Record<string, unknown>;
    dataset_type: string;
    doc_filename: string;
}

export interface InsightsTrendPoint {
    month_key: string;
    value: number;
}

export interface InsightsTrendMonth {
    month_key: string;
    points: Record<string, number>; // series_key -> value
    aggregates: {total?: number; avg_pct?: number};
}

export interface InsightsTrendSeries {
    series_key: string;
    metric_key: string;
    dataset_id: string;
    doc_filename: string;
    unit: string;
    points: InsightsTrendPoint[];
}

export interface InsightsMonthlyTrend {
    months: InsightsTrendMonth[];
    series: InsightsTrendSeries[];
}

export interface KnowledgeEntity {
    name: string;
    metric_key: string;
    value: number;
    doc_filename: string;
}

export interface AppliedOpAudit {
    op_id: string;
    op_type: string;
    op_label: string;
    target_node_label: string;
}

export interface KnowledgeSuggestion {
    id: string;
    type: "override" | "assumption" | "baseline_input";
    target_node_id: string;
    target_label: string;
    target_month: string;
    rationale: string;
    confidence: number;
    status: "proposed" | "approved" | "rejected" | "applied";
    provenance: Record<string, unknown>;
    applied_op_ids?: string[];
    // Audit trail (present when status === "applied")
    applied_at?: string;
    applied_by?: string;
    applied_ops?: AppliedOpAudit[];
}

export interface SuggestionPreviewData {
    suggestion: KnowledgeSuggestion;
    proposed_op: Record<string, unknown>;
    deltas: {cell: string; node_label: string; month_key: string; before: string; after: string; delta: string; delta_pct: string | null}[];
    summary_deltas: Record<string, {before: string; after: string; delta: string}>;
}

export interface InsightsNarrativeReport {
    id: string;
    status: "generating" | "done" | "failed";
    content_markdown: string;
    inputs_hash: string;
    generated_at: string | null;
}

export interface InsightsCatalogData {
    contract_version: number;
    datasets: KnowledgeDataset[];
    kpis: KnowledgeMetricKPI[];
    monthly_trend: InsightsMonthlyTrend;
    top_entities: KnowledgeEntity[];
    suggestions: KnowledgeSuggestion[];
    has_narrative: boolean;
    narrative_stale: boolean;
}

// ── Insights Hook ────────────────────────────────────────────

export function useVNextInsights(workspaceId: string | null) {
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/insights/` : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<InsightsCatalogData>(
        url,
        apiFetcher,
        {dedupingInterval: 30000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Insights Mutations ───────────────────────────────────────

export async function extractStructuredData(
    workspaceId: string,
    docId: string,
): Promise<{dataset: KnowledgeDataset; metrics: {metric_key: string; label: string; value: number; meta: Record<string, unknown>}[]; metrics_count: number}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/knowledge/${docId}/extract-structured/`,
        {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Structured extraction failed");
    }
    const json = await response.json();
    return json.data;
}

export async function approveSuggestion(
    workspaceId: string,
    suggestionId: string,
): Promise<KnowledgeSuggestion> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/insights/suggestions/${suggestionId}/approve/`,
        {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to approve suggestion");
    }
    const json = await response.json();
    return json.data.suggestion;
}

export async function rejectSuggestion(
    workspaceId: string,
    suggestionId: string,
): Promise<KnowledgeSuggestion> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/insights/suggestions/${suggestionId}/reject/`,
        {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to reject suggestion");
    }
    const json = await response.json();
    return json.data.suggestion;
}

export async function applySuggestion(
    workspaceId: string,
    suggestionId: string,
): Promise<{suggestion: KnowledgeSuggestion; op: VNextOp}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/insights/suggestions/${suggestionId}/apply/`,
        {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to apply suggestion");
    }
    const json = await response.json();
    return json.data;
}

export async function previewApplySuggestion(
    workspaceId: string,
    suggestionId: string,
): Promise<SuggestionPreviewData> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/insights/suggestions/${suggestionId}/preview-apply/`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify({domain: "pnl", months: 6}),
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to preview suggestion");
    }
    const json = await response.json();
    return json.data;
}

export async function generateNarrative(
    workspaceId: string,
    force?: boolean,
): Promise<{report: InsightsNarrativeReport; cached: boolean}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/insights/generate-narrative/`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify({force: force ?? false}),
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Narrative generation failed");
    }
    const json = await response.json();
    return json.data;
}

// ── Contact Mapping Types ──────────────────────────────────────

export interface ContactMappingEntry {
    contact_id: string;
    contact_name: string;
    is_customer: boolean;
    is_supplier: boolean;
    mapping: {
        id: string;
        segment: string;
        parent_contact_id: string;
        parent_contact_name: string;
        mapping_source: string;
        status: string;
    } | null;
}

export interface ContactMappingStats {
    total: number;
    mapped: number;
    unmapped: number;
    proposed: number;
}

export interface ContactMappingData {
    contacts: ContactMappingEntry[];
    stats: ContactMappingStats;
    segments: string[];
    parent_names: string[];
}

// ── Contact Mapping Hook ──────────────────────────────────────

export function useVNextContactMappings(
    workspaceId: string | null,
    type = "all",
    mappingStatus = "all",
    search = "",
) {
    const params = new URLSearchParams();
    if (type && type !== "all") params.set("type", type);
    if (mappingStatus && mappingStatus !== "all") params.set("status", mappingStatus);
    if (search) params.set("search", search);
    const qs = params.toString();
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/contact-mappings/${qs ? `?${qs}` : ""}`
        : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<ContactMappingData>(
        url,
        apiFetcher,
        {dedupingInterval: 30000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        contacts: data?.contacts ?? [],
        stats: data?.stats ?? {total: 0, mapped: 0, unmapped: 0, proposed: 0},
        segments: data?.segments ?? [],
        parentNames: data?.parent_names ?? [],
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Contact Mapping Mutations ──────────────────────────────────

export async function saveContactMapping(
    workspaceId: string,
    contactId: string,
    data: {segment?: string; parent_contact_name?: string; action?: "approve" | "reject"},
): Promise<{mapping: ContactMappingEntry["mapping"]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/contact-mappings/${contactId}/`,
        {
            method: "PUT",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify(data),
        },
    );
    if (!response.ok) throw new Error("Failed to save contact mapping");
    const json = await response.json();
    return json.data;
}

export async function deleteContactMapping(
    workspaceId: string,
    contactId: string,
): Promise<void> {
    await mutationFetch<unknown>(
        `/api/model-vnext/workspace/${workspaceId}/contact-mappings/${contactId}/`,
        {method: "DELETE", errorLabel: "Failed to delete contact mapping"},
    );
}

export async function bulkAssignSegment(
    workspaceId: string,
    contactIds: string[],
    segment: string,
): Promise<{updated: number; segment: string}> {
    return mutationFetch<{updated: number; segment: string}>(
        `/api/model-vnext/workspace/${workspaceId}/contact-mappings/bulk/`,
        {method: "POST", body: {contact_ids: contactIds, segment}, errorLabel: "Failed to bulk assign segment"},
    );
}

export async function uploadContactMappings(
    workspaceId: string,
    file: File,
): Promise<{created: number; updated: number; skipped: number; total_rows: number; warnings: string[]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/contact-mappings/upload/`,
        {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
            body: formData,
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || `Upload failed (${response.status})`);
    }
    const json = await response.json();
    return {...json.data, warnings: json.warnings ?? []};
}

export async function autoMapContacts(
    workspaceId: string,
    guidelines?: string,
    contactType?: "customer" | "supplier" | "all",
    unmappedOnly: boolean = true,
): Promise<{proposed: number; skipped: number; errors: string[]}> {
    return mutationFetch<{proposed: number; skipped: number; errors: string[]}>(
        `/api/model-vnext/workspace/${workspaceId}/contact-mappings/auto-map/`,
        {
            method: "POST",
            body: {
                guidelines: guidelines || "",
                contact_type: contactType || "all",
                unmapped_only: unmappedOnly,
            },
            errorLabel: "Auto-map contacts failed",
        },
    );
}

export async function bulkApproveContacts(
    workspaceId: string,
    contactIds: string[],
    action: "approve" | "reject" = "approve",
): Promise<{updated: number}> {
    return mutationFetch<{updated: number}>(
        `/api/model-vnext/workspace/${workspaceId}/contact-mappings/bulk/`,
        {method: "POST", body: {contact_ids: contactIds, action}, errorLabel: `Bulk ${action} failed`},
    );
}

export async function approveAllProposed(
    workspaceId: string,
): Promise<{updated: number}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/contact-mappings/bulk/`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify({action: "approve", all_proposed: true}),
        },
    );
    if (!response.ok) throw new Error("Approve all failed");
    const json = await response.json();
    return json.data;
}

export async function clearContactMappings(
    workspaceId: string,
    contactType: "all" | "customer" | "supplier" = "all",
): Promise<{deleted: number; contact_type: string}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/contact-mappings/clear/`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify({contact_type: contactType}),
        },
    );
    if (!response.ok) throw new Error("Failed to clear mappings");
    const json = await response.json();
    return json.data;
}

// ── Account Group Mapping ─────────────────────────────────────

export interface AccountGroupMappingEntry {
    account_id: string;
    account_code: string;
    account_name: string;
    account_type: string;
    reporting_code_name: string;
    mapping: {
        id: string;
        parent_group: string;
        account_group: string;
        reporting_code: string;
        reporting_code_name: string;
        mapping_source: string;
        status: string;
    } | null;
}

export interface AccountGroupMappingStats {
    total: number;
    mapped: number;
    unmapped: number;
    proposed: number;
}

export interface AccountGroupMappingData {
    accounts: AccountGroupMappingEntry[];
    stats: AccountGroupMappingStats;
    account_groups: string[];
    parent_groups: string[];
}

// ── Account Group Mapping Hook ────────────────────────────────

export function useVNextAccountGroupMappings(
    workspaceId: string | null,
    mappingStatus = "all",
    search = "",
    accountType = "",
) {
    const params = new URLSearchParams();
    if (mappingStatus && mappingStatus !== "all") params.set("status", mappingStatus);
    if (search) params.set("search", search);
    if (accountType && accountType !== "all_types") params.set("account_type", accountType);
    const qs = params.toString();
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/account-group-mappings/${qs ? `?${qs}` : ""}`
        : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<AccountGroupMappingData>(
        url,
        apiFetcher,
        {dedupingInterval: 30000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        accounts: data?.accounts ?? [],
        stats: data?.stats ?? {total: 0, mapped: 0, unmapped: 0, proposed: 0},
        accountGroups: data?.account_groups ?? [],
        parentGroups: data?.parent_groups ?? [],
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Account Group Mapping Mutations ───────────────────────────

export async function saveAccountGroupMapping(
    workspaceId: string,
    accountId: string,
    data: {parent_group?: string; account_group?: string; action?: "approve" | "reject"},
): Promise<{mapping: AccountGroupMappingEntry["mapping"]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/account-group-mappings/${accountId}/`,
        {
            method: "PUT",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify(data),
        },
    );
    if (!response.ok) throw new Error("Failed to save account group mapping");
    const json = await response.json();
    return json.data;
}

export async function deleteAccountGroupMapping(
    workspaceId: string,
    accountId: string,
): Promise<void> {
    await mutationFetch<unknown>(
        `/api/model-vnext/workspace/${workspaceId}/account-group-mappings/${accountId}/`,
        {method: "DELETE", errorLabel: "Failed to delete account group mapping"},
    );
}

export async function bulkAssignAccountGroup(
    workspaceId: string,
    accountIds: string[],
    parentGroup: string,
    accountGroup: string,
): Promise<{updated: number}> {
    return mutationFetch<{updated: number}>(
        `/api/model-vnext/workspace/${workspaceId}/account-group-mappings/bulk/`,
        {method: "POST", body: {account_ids: accountIds, parent_group: parentGroup, account_group: accountGroup}, errorLabel: "Failed to bulk assign account group"},
    );
}

export async function uploadAccountGroupMappings(
    workspaceId: string,
    file: File,
): Promise<{created: number; updated: number; skipped: number; total_rows: number; warnings: string[]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/account-group-mappings/upload/`,
        {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
            body: formData,
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || `Upload failed (${response.status})`);
    }
    const json = await response.json();
    return {...json.data, warnings: json.warnings ?? []};
}

export async function autoMapAccountGroups(
    workspaceId: string,
    guidelines?: string,
): Promise<{proposed: number; skipped: number; errors: string[]}> {
    return mutationFetch<{proposed: number; skipped: number; errors: string[]}>(
        `/api/model-vnext/workspace/${workspaceId}/account-group-mappings/auto-map/`,
        {method: "POST", body: {guidelines: guidelines || ""}, errorLabel: "Auto-map account groups failed"},
    );
}

// ── Transactions / Ledger ──────────────────────────────────────

export interface LedgerTransaction {
    id: string;
    date: string;
    journal_number: number | null;
    source_type: string;
    source_id: string;
    reference: string;
    line_number: number;
    account_code: string;
    account_name: string;
    account_type: string;
    account_id: string;
    description: string;
    contact_name: string;
    contact_id: string;
    net_amount: number;
    gross_amount: number;
    tax_amount: number;
    tax_type: string;
    debit: number;
    credit: number;
    tracking: Record<string, unknown>[];
}

export interface LedgerFilters {
    page?: number;
    page_size?: number;
    sort?: string;
    date_from?: string;
    date_to?: string;
    account_type?: string;
    account_code?: string;
    account_id?: string;
    source_type?: string;
    contact?: string;
    contact_id?: string;
    segment?: string;
    min_amount?: string;
    max_amount?: string;
    search?: string;
}

export interface LedgerData {
    transactions: LedgerTransaction[];
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    filters_applied: Record<string, unknown>;
    totals: {sum_debit: number; sum_credit: number; sum_net: number; count: number};
}

export interface TieOutSection {
    section: string;
    ledger_total: number;
    model_total: number;
    delta: number;
    abs_delta: number;
    status: "PASS" | "WARN" | "FAIL";
}

export interface TieOutData {
    month_key: string;
    sections: TieOutSection[];
    overall_status: "PASS" | "WARN" | "FAIL";
}

export function useVNextTieOut(workspaceId: string | null, monthKey: string | null) {
    const url =
        workspaceId && monthKey
            ? `/api/model-vnext/workspace/${workspaceId}/transactions/tie-out/?month_key=${monthKey}`
            : null;

    const {data, error, isLoading, mutate} = useSWR<TieOutData>(url, apiFetcher, {
        keepPreviousData: true,
    });

    return {
        data: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextTransactions(workspaceId: string | null, filters: LedgerFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== "") {
            params.set(key, String(value));
        }
    }
    const queryString = params.toString();
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/transactions/${queryString ? `?${queryString}` : ""}`
        : null;

    const {data, error, isLoading, mutate} = useSWR<LedgerData>(url, apiFetcher, {
        keepPreviousData: true,
    });

    return {
        data: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function reconcileTransactions(
    workspaceId: string,
    transactionIds: string[],
    isReconciled: boolean,
): Promise<{updated_count: number; is_reconciled: boolean}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/transactions/reconcile/`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify({transaction_ids: transactionIds, is_reconciled: isReconciled}),
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Reconciliation failed");
    }
    const json = await response.json();
    return json.data;
}

// ── P&L Explorer types & hooks ──────────────────────────────────

export interface PnLExplorerRow {
    key: string;
    label: string;
    is_expandable?: boolean;
    is_computed?: boolean;
    is_header?: boolean;
    is_pct?: boolean;
    expansion_blocked?: string;
    flow?: "inflow" | "outflow";
    quarters: Record<string, number>;
    months?: Record<string, number>;
    total: number;
    node_id?: string;
    // Transaction-level fields
    date?: string;
    description?: string;
    account_code?: string;
    account_name?: string;
    contact_name?: string;
    net_amount?: number;
    gross_amount?: number;
    tax_amount?: number;
    source_type?: string;
    source_id?: string;
    month_key?: string;
    type?: string;  // RECEIVE/SPEND/TRANSFER for CF transactions
}

export interface PnLCheckFigure {
    key: string;
    label: string;
    check_type?: string;
    is_computed?: boolean;
    quarters: Record<string, number>;
    months?: Record<string, number>;
    total: number;
    model_months?: Record<string, number>;
    model_quarters?: Record<string, number>;
    model_total?: number;
    xero_months?: Record<string, number>;
    xero_quarters?: Record<string, number>;
    xero_total?: number;
}

export interface PnLExplorerData {
    run_id: string | null;
    is_final?: boolean;
    source_mode?: string;
    level: string;
    quarters: string[];
    quarter_month_keys: Record<string, string[]>;
    rows: PnLExplorerRow[];
    check_figures?: PnLCheckFigure[];
    xero_check_figures?: PnLCheckFigure[];
    message?: string;
    month_status?: Record<string, "actual" | "forecast">;
    forecast_month_keys?: string[];
    // Snapshot diagnostics (section-level only)
    snapshot_info?: {
        facts_count: number | null;
        rollups_count: number | null;
        date_from: string | null;
        date_to: string | null;
        completeness_notes: string[];
        completed_at: string | null;
        distinct_accounts: string[];
        distinct_months: string[];
        facts_with_contact_id?: number;
        customer_parent_rollups?: number;
    };
    // Forecast diagnostics (section-level, when forecast is active)
    forecast_diagnostics?: {
        engine_node_count: number;
        snapshot_account_count: number;
        matched: number;
        orphan_forecasts: Array<{
            xero_account_id: string;
            label: string;
            section: string;
            forecast_total: number;
        }>;
        missing_forecasts: Array<{
            account_id: string;
            account_name: string;
            actual_total: number;
        }>;
    };
    // Drilldown warnings (empty rollups, wrong source_mode, etc.)
    drilldown_warnings?: string[];
    // Transaction pagination
    page?: number;
    page_size?: number;
    total?: number;
    // CF Explorer opening/closing cash
    opening_cash_balance?: number;
    opening_cash_date?: string;
}

export interface KPISnapshotRun {
    run_id: string;
    status: "pending" | "running" | "done" | "error";
    is_final?: boolean;
    completeness_notes?: string[];
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
    as_of?: string;
    mapping_version?: string;
    facts_count?: number;
    rollups_count?: number;
    checksum?: string;
    triggered_by?: string;
    started_at?: string;
    completed_at?: string;
    error_message?: string;
}

export interface PnLExplorerForecastParams extends PeriodOptions {
    scenarioId?: string;
    forecastMonths?: number;
    actualMonths?: number;
    currentMonth?: string;
    fyStartMonth?: number;
}

export function useVNextPnLExplorer(
    workspaceId: string | null,
    runId: string | null | undefined,
    level: string,
    filters: Record<string, string | undefined> = {},
    forecastParams?: PnLExplorerForecastParams,
) {
    const params = new URLSearchParams();
    if (runId) params.set("run_id", runId);
    params.set("level", level);
    for (const [k, v] of Object.entries(filters)) {
        if (v) params.set(k, v);
    }
    if (forecastParams) {
        appendPeriodParams(params, forecastParams);
        if (forecastParams.scenarioId) params.set("scenario_id", forecastParams.scenarioId);
        if (forecastParams.forecastMonths != null) params.set("forecast_months", String(forecastParams.forecastMonths));
        if (forecastParams.actualMonths) params.set("actual_months", String(forecastParams.actualMonths));
    }
    const qs = params.toString();
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/pnl-explorer/?${qs}`
        : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<PnLExplorerData>(
        url,
        apiFetcher,
        {keepPreviousData: true},
    );

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextCashFlowExplorer(
    workspaceId: string | null,
    runId: string | null | undefined,
    level: string,
    filters: Record<string, string | undefined> = {},
    forecastParams?: PnLExplorerForecastParams,
) {
    const params = new URLSearchParams();
    if (runId) params.set("run_id", runId);
    params.set("level", level);
    for (const [k, v] of Object.entries(filters)) {
        if (v) params.set(k, v);
    }
    if (forecastParams) {
        appendPeriodParams(params, forecastParams);
        if (forecastParams.scenarioId) params.set("scenario_id", forecastParams.scenarioId);
        if (forecastParams.forecastMonths != null) params.set("forecast_months", String(forecastParams.forecastMonths));
        if (forecastParams.actualMonths) params.set("actual_months", String(forecastParams.actualMonths));
    }
    const qs = params.toString();
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/cashflow-explorer/?${qs}`
        : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<PnLExplorerData>(
        url,
        apiFetcher,
        {keepPreviousData: true},
    );

    return {
        data: data ?? null,
        isLoading,
        isValidating,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

/**
 * Adapter hook: fetches CF Explorer section data and transforms it into
 * VNextMonth[] shape for consumers that previously used useVNextCashFlow.
 *
 * Uses KPI snapshots + Xero Bank Summary (same source as CF Explorer tab)
 * instead of the old VNext engine — fixes inflated balances.
 */
export function useVNextCashFlowFromExplorer(
    workspaceId: string | null,
    forecastParams?: PnLExplorerForecastParams,
) {
    const {data: explorerData, isLoading, error, refresh} = useVNextCashFlowExplorer(
        workspaceId, null, "section", {}, forecastParams,
    );

    const result = useMemo(() => {
        if (!explorerData?.rows?.length) return null;

        const fallbackMonths = explorerData.snapshot_info?.distinct_months
            ?? Array.from(
                new Set(
                    explorerData.rows.flatMap((row) => Object.keys(row.months ?? {})),
                ),
            );
        const sortedMonths = Object.keys(explorerData.month_status ?? {}).length
            ? Object.keys(explorerData.month_status ?? {}).sort()
            : [...fallbackMonths].sort();
        if (!sortedMonths.length) return null;
        const monthStatus = sortedMonths.reduce<Record<string, "actual" | "forecast">>(
            (acc, mk) => {
                acc[mk] = explorerData.month_status?.[mk] ?? "actual";
                return acc;
            },
            {},
        );

        const rowsByKey: Record<string, PnLExplorerRow> = {};
        for (const row of explorerData.rows) {
            rowsByKey[row.key] = row;
        }

        // Prefer category-based header rows (Phase 1 rebuild), fallback to
        // Xero Bank Summary check figures for legacy snapshots.
        const inflowRow = rowsByKey["cash_inflows"];
        const outflowRow = rowsByKey["cash_outflows"];
        const checkFigs = explorerData.xero_check_figures ?? [];
        const receivedFig = checkFigs.find((f) => f.key === "cash_received");
        const spentFig = checkFigs.find((f) => f.key === "cash_spent");

        const months: VNextMonth[] = sortedMonths.map((mk) => {
            const status = (monthStatus[mk] ?? "actual") as "actual" | "forecast";
            const [y, m] = mk.split("-").map(Number);
            const label = new Date(y, m - 1, 1).toLocaleString("en-US", {
                month: "short",
                year: "2-digit",
            });

            const rows: VNextRow[] = [];

            // Opening cash (from Xero Bank Summary via CF Explorer)
            rows.push({
                node_id: "__opening_cash__",
                label: "Opening Cash",
                category_key: "balance.opening",
                value: rowsByKey["opening_cash"]?.months?.[mk] ?? 0,
                status,
            });

            // Cash Received — prefer category-based inflow subtotal, fall back to check figures
            const received = inflowRow?.months?.[mk] ?? receivedFig?.xero_months?.[mk];
            if (received != null) {
                rows.push({
                    node_id: "__cash_received__",
                    label: "Cash Received",
                    category_key: "inflow.received",
                    value: received,
                    status,
                });
            }

            // Cash Spent — prefer category-based outflow subtotal (already negative), fall back to check figures
            const spent = outflowRow?.months?.[mk] ?? (spentFig?.xero_months?.[mk] != null ? -Math.abs(spentFig.xero_months[mk]) : undefined);
            if (spent != null) {
                rows.push({
                    node_id: "__cash_spent__",
                    label: "Cash Spent",
                    category_key: "outflow.spent",
                    value: spent,
                    status,
                });
            }

            // Net Cash Movement
            rows.push({
                node_id: "__net_movement__",
                label: "Net Cash Movement",
                category_key: "balance.net",
                value: rowsByKey["net_cash_flow"]?.months?.[mk] ?? 0,
                status,
            });

            // Closing cash (from Xero Bank Summary via CF Explorer)
            rows.push({
                node_id: "__closing_cash__",
                label: "Closing Cash",
                category_key: "balance.closing",
                value: rowsByKey["closing_cash"]?.months?.[mk] ?? 0,
                status,
            });

            return {month_key: mk, label, status, rows};
        });

        return {months};
    }, [explorerData]);

    return {
        data: result as {months: VNextMonth[]} | null,
        months: result?.months ?? [],
        isLoading,
        error,
        refresh,
    };
}

export function useVNextKPIRun(workspaceId: string | null, runId: string | null) {
    const url =
        workspaceId && runId
            ? `/api/model-vnext/workspace/${workspaceId}/kpi/${runId}/`
            : null;

    const {data, error, isLoading, mutate} = useSWR<KPISnapshotRun>(url, apiFetcher, {
        refreshInterval: (latestData: KPISnapshotRun | undefined) => {
            if (!latestData) return 2000;
            if (latestData.status === "pending" || latestData.status === "running") {
                return 2000;
            }
            return 0;
        },
    });

    return {
        data: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function runKPISnapshot(
    workspaceId: string,
    scenarioId?: string,
    sourceMode?: string,
): Promise<{run_id: string; status: string}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const body: Record<string, string> = {};
    if (scenarioId) body.scenario_id = scenarioId;
    if (sourceMode) body.source_mode = sourceMode;

    const response = await fetch(`/api/model-vnext/workspace/${workspaceId}/kpi/run/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
        credentials: "include",
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to create snapshot");
    }
    const json = await response.json();
    return json.data;
}

// ══════════════════════════════════════════════════════════════════
// ██  Sandbox Types & Hooks  ██
// ══════════════════════════════════════════════════════════════════

export interface SandboxUpload {
    id: string;
    filename: string;
    label: string;
    mime_type: string;
    size_bytes: number;
    parsed_row_count: number;
    extraction_status: string;
    parsed_schema_json: {columns?: {name: string; dtype: string; sample: string[]}[]};
    created_at: string | null;
}

export interface SandboxRun {
    id: string;
    parent_run_id: string | null;
    template: string;
    prompt: string;
    diff_summary: string;
    status: string;
    error_message: string;
    artifact_count?: number;
    insight_count?: number;
    started_at: string | null;
    completed_at: string | null;
    created_at: string | null;
}

export interface SandboxArtifact {
    id: string;
    run_id: string;
    artifact_type: string;
    title: string;
    subtitle: string;
    report_json: Record<string, unknown>;
    source_ids: string[];
    tab_target?: string;
    is_pinned: boolean;
    ordinal: number;
    created_at: string | null;
}

export interface SandboxDataSource {
    id: string;
    source_type: string;
    source_ref: Record<string, unknown>;
    label: string;
    schema_json: {columns?: {name: string; dtype: string; sample: string[]}[]};
    freshness_ts: string | null;
    status: string;
    metadata_json: Record<string, unknown>;
    created_at: string | null;
}

export interface SandboxInsight {
    id: string;
    run_id: string;
    artifact_id: string | null;
    insight_type: string;
    title: string;
    summary: string;
    confidence: number;
    proposed_op_json: Record<string, unknown>;
    source_excerpt: string;
    status: string;
    created_at: string | null;
}

export interface SandboxPromotion {
    id: string;
    insight_id: string;
    insight_title: string;
    op_id: string | null;
    op_label: string;
    promoted_at: string | null;
    notes: string;
}

// ── Sandbox Hooks ────────────────────────────────────────────────

export function useVNextSandboxUploads(workspaceId: string | null) {
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/sandbox/uploads/`
        : null;

    const {data, error, isLoading, mutate} = useSWR<{uploads: SandboxUpload[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );

    return {
        uploads: data?.uploads ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextSandboxRuns(workspaceId: string | null) {
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/sandbox/runs/`
        : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<{runs: SandboxRun[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        runs: data?.runs ?? [],
        isLoading,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextSandboxRunDetail(
    workspaceId: string | null,
    runId: string | null,
) {
    const url =
        workspaceId && runId
            ? `/api/model-vnext/workspace/${workspaceId}/sandbox/runs/${runId}/`
            : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<{
        run: SandboxRun;
        artifacts: SandboxArtifact[];
        insights: SandboxInsight[];
    }>(url, apiFetcher, {dedupingInterval: 30000, keepPreviousData: true});

    const showError = error && !(isValidating && data);

    return {
        run: data?.run ?? null,
        artifacts: data?.artifacts ?? [],
        insights: data?.insights ?? [],
        isLoading,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextSandboxPromotions(workspaceId: string | null) {
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/sandbox/promotions/`
        : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<{promotions: SandboxPromotion[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        promotions: data?.promotions ?? [],
        isLoading,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useVNextSandboxDataSources(workspaceId: string | null) {
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/sandbox/data-sources/`
        : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<{data_sources: SandboxDataSource[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        dataSources: data?.data_sources ?? [],
        isLoading,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function registerSandboxDataSources(
    workspaceId: string,
): Promise<{registered: number; data_sources: SandboxDataSource[]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/data-sources/register/`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to register data sources");
    }
    const json = await response.json();
    return {registered: json.data.registered, data_sources: json.data.data_sources};
}

export async function refreshSandboxDataSources(
    workspaceId: string,
    sourceId?: string,
): Promise<{refreshed: number; data_sources: SandboxDataSource[]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/data-sources/refresh/`,
        {
            method: "POST",
            headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
            credentials: "include",
            body: JSON.stringify(sourceId ? {source_id: sourceId} : {}),
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to refresh data sources");
    }
    const json = await response.json();
    return {refreshed: json.data.refreshed, data_sources: json.data.data_sources};
}

// ── Sandbox Mutations ────────────────────────────────────────────

export async function uploadSandboxFile(
    workspaceId: string,
    file: File,
): Promise<{upload: SandboxUpload; warnings: string[]}> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/uploads/upload/`,
        {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
            body: formData,
        },
    );
    if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.errors?.[0] || "Failed to upload file");
    }
    const json = await response.json();
    return {upload: json.data.upload, warnings: json.warnings || []};
}

export async function deleteSandboxUpload(
    workspaceId: string,
    uploadId: string,
): Promise<void> {
    await mutationFetch<unknown>(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/uploads/${uploadId}/`,
        {method: "DELETE", errorLabel: "Failed to delete upload"},
    );
}

export async function runSandboxAnalysis(
    workspaceId: string,
    params: {upload_ids: string[]; template?: string; prompt?: string},
): Promise<{run: SandboxRun}> {
    const result = await mutationFetch<{run: SandboxRun}>(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/runs/`,
        {method: "POST", body: params, errorLabel: "Failed to start analysis"},
    );
    return {run: result.run};
}

export async function toggleSandboxPin(
    workspaceId: string,
    artifactId: string,
    isPinned: boolean,
): Promise<SandboxArtifact> {
    const result = await mutationFetch<{artifact: SandboxArtifact}>(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/artifacts/${artifactId}/`,
        {method: "PATCH", body: {is_pinned: isPinned}, errorLabel: "Failed to update artifact"},
    );
    return result.artifact;
}

export async function deleteSandboxArtifact(
    workspaceId: string,
    artifactId: string,
): Promise<void> {
    await mutationFetch<unknown>(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/artifacts/${artifactId}/`,
        {method: "DELETE", errorLabel: "Failed to delete artifact"},
    );
}

export async function dismissSandboxInsight(
    workspaceId: string,
    insightId: string,
): Promise<SandboxInsight> {
    const result = await mutationFetch<{insight: SandboxInsight}>(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/insights/${insightId}/dismiss/`,
        {method: "POST", errorLabel: "Failed to dismiss insight"},
    );
    return result.insight;
}

export async function previewPromoteSandboxInsight(
    workspaceId: string,
    insightId: string,
    domain = "pnl",
    months = 6,
): Promise<{preview: Record<string, unknown>}> {
    const result = await mutationFetch<{preview: Record<string, unknown>}>(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/insights/${insightId}/preview-promote/`,
        {method: "POST", body: {domain, months}, errorLabel: "Failed to preview promotion"},
    );
    return {preview: result.preview};
}

export async function promoteSandboxInsight(
    workspaceId: string,
    insightId: string,
    notes?: string,
): Promise<{promotion: SandboxPromotion; op: Record<string, unknown>}> {
    const result = await mutationFetch<{promotion: SandboxPromotion; op: Record<string, unknown>}>(
        `/api/model-vnext/workspace/${workspaceId}/sandbox/insights/${insightId}/promote/`,
        {method: "POST", body: {notes: notes || ""}, errorLabel: "Failed to promote insight"},
    );
    return {promotion: result.promotion, op: result.op};
}

export function useVNextTabArtifacts(workspaceId: string | null, tabTarget: string) {
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/sandbox/artifacts/?tab_target=${tabTarget}`
        : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<{artifacts: SandboxArtifact[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 5000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        artifacts: data?.artifacts ?? [],
        isLoading,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── Accuracy Audit ─────────────────────────────────────────────

export interface AuditCheck {
    label: string;
    xero: number | null;
    engine: number | null;
    delta: number | null;
    pass: boolean | null;
    note: string;
}

export interface AuditMonth {
    month_key: string;
    label: string;
    has_pnl_snapshot: boolean;
    has_bs_snapshot: boolean;
    has_cash_snapshot: boolean;
    pass: boolean;
    checks: AuditCheck[];
}

export interface AuditData {
    overall_pass: boolean;
    months_audited: number;
    months_with_pnl_snapshot: number;
    months_with_cash_snapshot: number;
    months: AuditMonth[];
    tolerance: number;
    audited_at: string;
}

export function useVNextAudit(workspaceId: string | null) {
    const url = workspaceId ? `/api/model-vnext/workspace/${workspaceId}/audit/` : null;

    const {data, error, isLoading, mutate} = useSWR<{status: string; data: AuditData}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );

    return {
        audit: data?.data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

// ── CEO Snapshot ───────────────────────────────────────────────

export interface CEOSnapshotCards {
    revenue: number;
    cogs: number;
    gross_profit: number;
    gross_margin_pct: number;
    net_profit: number;
    net_margin_pct: number;
    opex: number;
    closing_cash: number | null;
    closing_cash_month: string | null;
    net_cash_movement: number;
    ncf_months: string[];
    dso: number | null;
    total_ar: number | null;
    top5_concentration_pct: number | null;
    customer_count: number;
    revenue_mom_pct: number | null;
}

export interface CEOSnapshotTrend {
    month_key: string;
    status: string;
    revenue: number;
    costs: number;
    net: number;
    source: string;
}

export interface CEOSnapshotData {
    cards: CEOSnapshotCards;
    top_customers: {name: string; revenue: number}[];
    trend: CEOSnapshotTrend[];
    period: {
        actual_months: number;
        forecast_months: number;
        actuals_source: string;
        period_month_keys: string[];
    };
}

// ── CEO Summary V2 (single-endpoint) ────────────────────────────

export interface CEOSummaryV2Card {
    key: string;
    label: string;
    mtd: number | null;
    ytd: number | null;
    py_ytd: number | null;
    ytd_vs_py_pct: number | null;
    format: "currency" | "percent";
}

export interface CEOSummaryV2Scorecard {
    month_key: string;
    revenue: number;
    cogs: number;
    gross_profit: number;
    gross_margin_pct: number;
    opex: number;
    net_profit: number;
    net_margin_pct: number;
    closing_cash: number;
    net_cash_movement: number;
}

export interface CEOSummaryV2Trend {
    month_key: string;
    revenue: number;
    net_profit: number;
    closing_cash: number;
}

export interface CEOSummaryV2Data {
    period: {
        current_month: string;
        period_mode: string;
        fy_start_month: number;
        period_label: string;
        months_with_data?: number;
        months_total?: number;
        data_note?: string | null;
        ytd_months: string[];
        py_ytd_months: string[];
    };
    cards: CEOSummaryV2Card[];
    scorecard: CEOSummaryV2Scorecard[];
    trend: CEOSummaryV2Trend[];
}

export interface CEOSummaryV2Opts {
    period?: string;
    selectedMonth?: string;
    currentMonth?: string;
    fyStartMonth?: number;
    customFrom?: string;
    customTo?: string;
}

export function useVNextCEOSummaryV2(workspaceId?: string, opts?: CEOSummaryV2Opts) {
    const qs = new URLSearchParams();
    if (opts?.period) qs.set("period", opts.period);
    if (opts?.selectedMonth) qs.set("selected_month", opts.selectedMonth);
    if (opts?.currentMonth) qs.set("current_month", opts.currentMonth);
    if (opts?.fyStartMonth) qs.set("fy_start_month", String(opts.fyStartMonth));
    if (opts?.customFrom) qs.set("custom_from", opts.customFrom);
    if (opts?.customTo) qs.set("custom_to", opts.customTo);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";

    const {data, error, isLoading, isValidating, mutate} = useSWR<CEOSummaryV2Data>(
        workspaceId ? `/api/model-vnext/workspace/${workspaceId}/ceo-summary-v2/${suffix}` : null,
        apiFetcher,
        {dedupingInterval: 5000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        data: data ?? null,
        error: showError ? friendlyErrorMessage(error) : null,
        isLoading,
        refresh: mutate,
    };
}

export function useVNextCEOSnapshot(
    workspaceId: string | null,
    scenarioId?: string,
    months = 12,
    periodOpts?: PeriodOptions,
) {
    const params = new URLSearchParams({months: String(months)});
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (periodOpts?.period) params.set("period", periodOpts.period);
    if (periodOpts?.selectedMonth) params.set("selected_month", periodOpts.selectedMonth);
    if (periodOpts?.customFrom) params.set("custom_from", periodOpts.customFrom);
    if (periodOpts?.customTo) params.set("custom_to", periodOpts.customTo);

    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/ceo-snapshot/?${params}`
        : null;

    const {data, error, isLoading, isValidating, mutate} = useSWR<{status: string; data: CEOSnapshotData}>(
        url,
        apiFetcher,
        {dedupingInterval: 5000, keepPreviousData: true},
    );

    const showError = error && !(isValidating && data);

    return {
        snapshot: data?.data ?? null,
        isLoading,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export type RevenueDriverType =
    | "growth"
    | "fixed"
    | "last_year_curve"
    | "seasonality"
    | "step"
    | "flat";

export type RevenueScopeType = "customer" | "parent" | "segment";
export type CostScopeType = "supplier" | "account" | "account_group" | "parent_group";

export interface RevenueForecastTemplate {
    id: string;
    name: string;
    description: string;
    driver_type: RevenueDriverType;
    params: Record<string, unknown>;
    is_archived: boolean;
    created_at: string;
    updated_at: string;
}

export interface CustomerRevenueAssumption {
    id: string;
    scope_type: RevenueScopeType;
    scope_id: string;
    scope_label: string;
    template_id: string | null;
    driver_type: RevenueDriverType | "";
    params: Record<string, unknown>;
    month_overrides: Record<string, number>;
    is_active: boolean;
    notes: string;
    created_by: string;
    effective_driver_type: RevenueDriverType;
    effective_params: Record<string, unknown>;
    created_at: string;
    updated_at: string;
}

export interface CostForecastTemplate {
    id: string;
    name: string;
    description: string;
    driver_type: RevenueDriverType;
    params: Record<string, unknown>;
    is_archived: boolean;
    created_at: string;
    updated_at: string;
}

export interface CostAssumption {
    id: string;
    scenario_id: string;
    pnl_section: "cogs" | "expenses";
    scope_type: CostScopeType;
    scope_id: string;
    scope_label: string;
    account_id: string;
    template_id: string | null;
    driver_type: RevenueDriverType | "";
    params: Record<string, unknown>;
    month_overrides: Record<string, number>;
    is_active: boolean;
    notes: string;
    created_by: string;
    effective_driver_type: RevenueDriverType;
    effective_params: Record<string, unknown>;
    created_at: string;
    updated_at: string;
}

export interface RevenueCustomerRow {
    id: string;
    name: string;
    driver_type: RevenueDriverType | "";
    params: Record<string, unknown>;
    has_assumption: boolean;
    actuals: Record<string, number>;
    forecast: Record<string, number>;
    months: Record<string, number>;
}

export interface RevenueParentRow {
    id: string;
    name: string;
    months: Record<string, number>;
    children: RevenueCustomerRow[];
}

export interface RevenueSegmentRow {
    label: string;
    months: Record<string, number>;
    parents: RevenueParentRow[];
}

export interface RevenueAccountRow {
    account_node_id: string;
    xero_account_id: string;
    account_code: string;
    label: string;
    category_key: string;
    months: Record<string, number>;
    segments: RevenueSegmentRow[];
}

export interface RevenueBottomUpData {
    accounts: RevenueAccountRow[];
    segments: RevenueSegmentRow[];
    grand_total: Record<string, number>;
    months: string[];
    actual_months: string[];
    forecast_months: string[];
    assumption_count: number;
}

export interface CostResidualRow {
    months: Record<string, number>;
}

export interface CostSupplierRow {
    id: string;
    name: string;
    months: Record<string, number>;
    has_assumption: boolean;
    driver_type: RevenueDriverType | "";
}

export interface CostAccountRow {
    id: string;
    node_id?: string;
    code: string;
    name: string;
    months: Record<string, number>;
    has_assumption: boolean;
    driver_type: RevenueDriverType | "";
    suppliers: CostSupplierRow[];
    residual?: CostResidualRow | null;
}

export interface CostAccountGroupRow {
    label: string;
    months: Record<string, number>;
    accounts: CostAccountRow[];
    residual?: CostResidualRow | null;
}

export interface CostParentGroupRow {
    label: string;
    pnl_section: "cogs" | "expenses" | string;
    months: Record<string, number>;
    account_groups: CostAccountGroupRow[];
    residual?: CostResidualRow | null;
}

export interface CostBottomUpData {
    parent_groups: CostParentGroupRow[];
    grand_total: Record<string, number>;
    months: string[];
    actual_months: string[];
    forecast_months: string[];
    assumption_count: number;
}

export function useRevenueTemplates(workspaceId: string | null) {
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/revenue-templates/`
        : null;
    const {data, error, isLoading, mutate} = useSWR<{templates: RevenueForecastTemplate[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );
    return {
        templates: data?.templates ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useCustomerAssumptions(
    workspaceId: string | null,
    scenarioId?: string,
) {
    const params = scenarioId ? `?scenario_id=${scenarioId}` : "";
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/customer-assumptions/${params}`
        : null;
    const {data, error, isLoading, mutate} = useSWR<{assumptions: CustomerRevenueAssumption[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 10000},
    );
    return {
        assumptions: data?.assumptions ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useCostTemplates(workspaceId: string | null) {
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/cost-templates/`
        : null;
    const {data, error, isLoading, mutate} = useSWR<{templates: CostForecastTemplate[]}>(
        url,
        apiFetcher,
        {dedupingInterval: 30000},
    );
    return {
        templates: data?.templates ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useCostAssumptions(
    workspaceId: string | null,
    scenarioId?: string,
    pnlSection?: "cogs" | "expenses",
) {
    const params = new URLSearchParams();
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (pnlSection) params.set("pnl_section", pnlSection);
    const suffix = params.toString() ? `?${params}` : "";
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/cost-assumptions/${suffix}`
        : null;
    const {data, error, isLoading, mutate} = useSWR<{assumptions: CostAssumption[]; scenario_id?: string}>(
        url,
        apiFetcher,
        {dedupingInterval: 10000},
    );
    return {
        assumptions: data?.assumptions ?? [],
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useRevenueBottomUp(
    workspaceId: string | null,
    scenarioId?: string,
    actualMonths = 12,
    forecastMonths = 12,
) {
    const params = new URLSearchParams({
        actual_months: String(actualMonths),
        forecast_months: String(forecastMonths),
    });
    if (scenarioId) params.set("scenario_id", scenarioId);
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/revenue-bottom-up/?${params}`
        : null;
    const {data, error, isLoading, isValidating, mutate} = useSWR<RevenueBottomUpData>(
        url,
        apiFetcher,
        {dedupingInterval: 15000, keepPreviousData: true},
    );
    const showError = error && !(isValidating && data);
    return {
        grid: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export function useCostBottomUp(
    workspaceId: string | null,
    scenarioId?: string,
    actualMonths = 12,
    forecastMonths = 12,
    pnlSection?: "cogs" | "expenses",
) {
    const params = new URLSearchParams({
        actual_months: String(actualMonths),
        forecast_months: String(forecastMonths),
    });
    if (scenarioId) params.set("scenario_id", scenarioId);
    if (pnlSection) params.set("pnl_section", pnlSection);
    const url = workspaceId
        ? `/api/model-vnext/workspace/${workspaceId}/cost-bottom-up/?${params}`
        : null;
    const {data, error, isLoading, isValidating, mutate} = useSWR<CostBottomUpData>(
        url,
        apiFetcher,
        {dedupingInterval: 15000, keepPreviousData: true},
    );
    const showError = error && !(isValidating && data);
    return {
        grid: data ?? null,
        isLoading,
        isValidating,
        error: showError ? friendlyErrorMessage(error) : null,
        refresh: mutate,
    };
}

export async function createRevenueTemplate(
    workspaceId: string,
    payload: {name: string; driver_type: RevenueDriverType; params?: Record<string, unknown>; description?: string},
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/revenue-templates/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new ApiError(`Template create failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function updateRevenueTemplate(
    workspaceId: string,
    templateId: string,
    patch: Partial<{name: string; description: string; driver_type: RevenueDriverType; params: Record<string, unknown>; is_archived: boolean}>,
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/revenue-templates/${templateId}/`, {
        method: "PATCH",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw new ApiError(`Template update failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function archiveRevenueTemplate(workspaceId: string, templateId: string) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/revenue-templates/${templateId}/`, {
        method: "DELETE",
        headers: {"X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
    });
    if (!res.ok) throw new ApiError(`Template archive failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function saveCustomerAssumption(
    workspaceId: string,
    payload: {
        scenario_id: string;
        scope_type: RevenueScopeType;
        scope_id: string;
        scope_label?: string;
        driver_type?: RevenueDriverType;
        params?: Record<string, unknown>;
        month_overrides?: Record<string, number>;
        template_id?: string;
        notes?: string;
    },
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/customer-assumptions/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new ApiError(`Assumption save failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function updateCustomerAssumption(
    workspaceId: string,
    assumptionId: string,
    patch: Partial<{driver_type: RevenueDriverType; params: Record<string, unknown>; month_overrides: Record<string, number>; notes: string; template_id: string | null}>,
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/customer-assumptions/${assumptionId}/`, {
        method: "PATCH",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw new ApiError(`Assumption update failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function deleteCustomerAssumption(workspaceId: string, assumptionId: string) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/customer-assumptions/${assumptionId}/`, {
        method: "DELETE",
        headers: {"X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
    });
    if (!res.ok) throw new ApiError(`Assumption delete failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function createCostTemplate(
    workspaceId: string,
    payload: {name: string; driver_type: RevenueDriverType; params?: Record<string, unknown>; description?: string},
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/cost-templates/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new ApiError(`Cost template create failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function updateCostTemplate(
    workspaceId: string,
    templateId: string,
    patch: Partial<{name: string; description: string; driver_type: RevenueDriverType; params: Record<string, unknown>; is_archived: boolean}>,
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/cost-templates/${templateId}/`, {
        method: "PATCH",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw new ApiError(`Cost template update failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function archiveCostTemplate(workspaceId: string, templateId: string) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/cost-templates/${templateId}/`, {
        method: "DELETE",
        headers: {"X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
    });
    if (!res.ok) throw new ApiError(`Cost template archive failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function saveCostAssumption(
    workspaceId: string,
    payload: {
        scenario_id: string;
        pnl_section: "cogs" | "expenses";
        scope_type: CostScopeType;
        scope_id: string;
        scope_label?: string;
        account_id?: string;
        driver_type?: RevenueDriverType;
        params?: Record<string, unknown>;
        month_overrides?: Record<string, number>;
        template_id?: string | null;
        notes?: string;
    },
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/cost-assumptions/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new ApiError(`Cost assumption save failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function updateCostAssumption(
    workspaceId: string,
    assumptionId: string,
    patch: Partial<{driver_type: RevenueDriverType; params: Record<string, unknown>; month_overrides: Record<string, number>; notes: string; template_id: string | null}>,
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/cost-assumptions/${assumptionId}/`, {
        method: "PATCH",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw new ApiError(`Cost assumption update failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function deleteCostAssumption(workspaceId: string, assumptionId: string) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/cost-assumptions/${assumptionId}/`, {
        method: "DELETE",
        headers: {"X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
    });
    if (!res.ok) throw new ApiError(`Cost assumption delete failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function costBulkApply(
    workspaceId: string,
    payload: {
        scenario_id: string;
        pnl_section: "cogs" | "expenses";
        scope_type: "parent_group" | "account_group" | "account";
        scope_id: string;
        driver_type?: RevenueDriverType;
        template_id?: string;
        params?: Record<string, unknown>;
        month_overrides?: Record<string, number>;
        notes?: string;
    },
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/cost-bulk-apply/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new ApiError(`Cost bulk apply failed (${res.status})`, "server", res.status);
    return res.json();
}

export async function revenueBulkApply(
    workspaceId: string,
    payload: {
        scenario_id: string;
        scope_type: "segment" | "parent";
        scope_id: string;
        driver_type?: RevenueDriverType;
        template_id?: string;
        params?: Record<string, unknown>;
        notes?: string;
    },
) {
    const res = await fetch(`/api/model-vnext/workspace/${workspaceId}/revenue-bulk-apply/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", "X-CSRFToken": Cookies.get("csrftoken") ?? ""},
        credentials: "include",
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new ApiError(`Bulk apply failed (${res.status})`, "server", res.status);
    return res.json();
}

// ── EOH Cancellations by Client ─────────────────────────────

export interface EohCancellationRow {
    client: string;
    total: number;
    [monthKey: string]: string | number;
}

export interface EohCancellationsData {
    months: string[];
    rows: EohCancellationRow[];
}

export function useEohCancellationsByClient() {
    const {data, error, isLoading} = useSWR<EohCancellationsData>(
        "/api/eoh/cancellations-by-client/",
        apiFetcher,
    );
    return {
        data: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
    };
}

// ── EOH Bookings Summary (combined) ────────────────────────

export interface EohPivotRow {
    [key: string]: string | number;
    total: number;
}

export interface EohFillRateRow {
    month: string;
    requested: number;
    filled: number;
    fill_pct: number;
}

export interface EohWeeklyRow {
    week: string;
    fy_week: string;
    requested: number;
    filled: number;
    fill_pct: number;
    hours: number;
    revenue: number;
    is_future: boolean;
}

export interface EohUnfillRow {
    reason: string;
    count: number;
}

export interface EohClientVolumeRow {
    client: string;
    total_hours: number;
    total_shifts: number;
    total_revenue: number;
    [key: string]: string | number;
}

export interface EohConcentrationRow {
    client: string;
    hours: number;
    shifts: number;
    revenue: number;
    pct: number;
    cumulative_pct: number;
}

export interface EohChurnRow {
    client: string;
    last_booked: string;
    days_since: number;
    band: string;
}

export interface EohForwardRow {
    month?: string;
    week?: string;
    fy_week?: string;
    shifts: number;
    hours: number;
    revenue: number;
}

export interface EohClientTrendRow {
    client: string;
    trend: "growing" | "stable" | "declining" | "churned";
    current_weekly_avg_hours: number;
    prior_period_weekly_avg_hours: number;
    pct_change: number | null;
    last_booked: string | null;
    weeks_active_last_13: number;
}

export interface EohSnapshotKpis {
    future_booked_shifts: number;
    future_booked_hours: number;
    future_est_revenue: number;
    current_fy_week: string;
    current_week_fill_pct: number;
    current_week_cancellations: number;
    active_clients_this_week: number;
    top_5_client_concentration_pct: number;
    clients_no_booking_30d: number;
    declining_or_churned_clients: number;
    latest_shift_synced_at: string | null;
}

export interface EohAlertRow {
    severity: "success" | "info" | "warning";
    title: string;
    detail: string;
}

export interface EohForwardChartPoint {
    fy_week: string;
    label: string;
    week_start: string;
    shifts: number;
    hours: number;
    revenue: number;
}

export interface EohFillRateChartPoint {
    fy_week: string;
    label: string;
    week_start: string;
    requested: number;
    filled: number;
    fill_pct: number;
    is_future: boolean;
}

export interface EohStatusMixPoint {
    status: string;
    shifts: number;
}

export interface EohTopClientPoint {
    client: string;
    shifts: number;
    hours: number;
    revenue: number;
}

export interface EohChartSeries {
    forward_hours_by_week: EohForwardChartPoint[];
    fill_rate_by_week: EohFillRateChartPoint[];
    status_mix: EohStatusMixPoint[];
    top_clients_by_hours: EohTopClientPoint[];
}

export interface EohDailyActivityRow {
    date: string;
    requested: number;
    filled: number;
    cancelled: number;
    hours: number;
    revenue: number;
    active_clients: number;
}

export interface EohYoYRow {
    month_num: string;
    [key: string]: string | number;
}

export interface EohBookingsSummaryData {
    months: string[];
    weeks: string[];
    fy_start_month: number;
    fy_weeks: string[];
    fy_week_starts: Record<string, string>;
    charge_rate: number;
    snapshot_kpis: EohSnapshotKpis;
    executive_summary: string[];
    chart_series: EohChartSeries;
    daily_activity: EohDailyActivityRow[];
    alerts: EohAlertRow[];
    fill_rate: EohFillRateRow[];
    fill_rate_weekly: EohWeeklyRow[];
    hours_by_qualification: EohPivotRow[];
    hours_by_qualification_weekly: EohPivotRow[];
    cancellations_by_qualification: EohPivotRow[];
    cancellations_by_qualification_weekly: EohPivotRow[];
    cancellations_by_client: EohPivotRow[];
    cancellations_by_client_weekly: EohPivotRow[];
    status_funnel: EohPivotRow[];
    status_funnel_weekly: EohPivotRow[];
    status_hours: EohPivotRow[];
    status_hours_weekly: EohPivotRow[];
    unfill_reasons: EohUnfillRow[];
    client_volume: EohClientVolumeRow[];
    client_volume_weekly: EohClientVolumeRow[];
    client_concentration: EohConcentrationRow[];
    churn: EohChurnRow[];
    client_trends: EohClientTrendRow[];
    forward_bookings: EohForwardRow[];
    forward_bookings_weekly: EohForwardRow[];
    yoy_comparison: EohYoYRow[];
    yoy_years: string[];
}

export function useEohBookingsSummary() {
    const {data, error, isLoading} = useSWR<EohBookingsSummaryData>(
        "/api/eoh/bookings-summary/",
        apiFetcher,
    );
    return {
        data: data ?? null,
        isLoading,
        error: error ? friendlyErrorMessage(error) : null,
    };
}
